//#region IMPORT
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import dayjs from 'dayjs';
/** Services Import */
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
/** Components */
import { Loading } from '../../../../Components/MUIs';
import useDelete from '../../../../hooks/useDeleteN';
import useActionLog from '../../../../hooks/useActionLog';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
/** Icons Import */
import SAM_BASIC_02 from './SAM_BASIC_02';
import { applyExpandedDetailRowHeight } from './SAM_BASIC_02_DetailHeight';
import WorkTypeItemManagement from './WorkTypeItemManagement';
import {
  useSearch1,
  useSearch2,
  useSearch3,
  useSave2,
  useSave3,
  useDelete1,
  useDelete2,
  useSearch4,
  useSearch5,
} from './SAM_BASIC_02_QUERY';
import { useSaveWorkTypeItems } from './SAM_BASIC_02_WorkType_QUERY';
import {
  getApprovalGubunForRow,
  getApprovalGubunForDetailRows,
  getPermitStatusAndButton,
  canDetailApproveReject,
} from './SAM_BASIC_02_StatusHelper';
import { PDFDocument } from 'pdf-lib';
import { flushSync } from 'react-dom';
//#endregion

// 화면 MENU_ID
const SCREEN_MENU_ID = 'SafetyWorkManagement';

/** 목록 행 → SavePerNewU3 PmCd (camelCase / PascalCase / UPPER_SNAKE) */
function ResolvePmCdForPermitSave(row) {
  if (!row || typeof row !== 'object') return '';
  return row.pmCd ?? row.PmCd ?? row.PM_CD ?? '';
}

/** SavePerNewU3 — 작업일자 yyyy-MM-dd */
function PickPermitWorkDateYmd(row) {
  if (!row || typeof row !== 'object') return '';
  const raw =
    row.workDate ??
    row.WorkDate ??
    row.createDate ??
    row.CreateDate ??
    '';
  return raw ? String(raw).trim().substring(0, 10) : '';
}

/** SavePerNewU3 — 일자별 코드 */
function PickPermitDailyCd(row) {
  if (!row || typeof row !== 'object') return '';
  return String(row.dailyCd ?? row.DailyCd ?? row.DAILY_CD ?? '').trim();
}

function FindMasterDetailRows(pmCd, rowData1) {
  const key = String(pmCd ?? '').trim();
  if (!key || !Array.isArray(rowData1)) return [];
  const master = rowData1.find(
    (r) => String(ResolvePmCdForPermitSave(r)).trim() === key
  );
  return master?.detailRows ?? [];
}

function ResolveDetailDtlCd(row) {
  return String(row?.dtlCd ?? row?.DtlCd ?? row?.DTL_CD ?? '').trim();
}

function ResolveDetailDailyCd(row) {
  return String(row?.dailyCd ?? row?.DailyCd ?? row?.DAILY_CD ?? '').trim();
}

/** DAILY_CD ↔ 작업일자 매핑 (DailiesByPmCd 기준) */
function BuildDailyCdByWorkDateMap(dailies) {
  const map = new Map();
  for (const daily of AsPerNewArray(dailies)) {
    const wdRaw = daily?.workDate ?? daily?.WorkDate ?? '';
    const ymd =
      wdRaw && dayjs(wdRaw).isValid()
        ? dayjs(wdRaw).format('YYYY-MM-DD')
        : String(wdRaw).trim().substring(0, 10);
    const dc = String(daily?.dailyCd ?? daily?.DailyCd ?? '').trim();
    if (ymd && dc) map.set(ymd, dc);
  }
  return map;
}

/** ResolveDetailDayKey — StatusHelper와 동일(작업일자 우선) */
function ResolveDetailDayKey(row) {
  const ymd = PickPermitWorkDateYmd(row);
  if (ymd) return `w:${ymd}`;
  const dailyCd = ResolveDetailDailyCd(row);
  if (dailyCd) return `d:${dailyCd}`;
  return '';
}

/** 세부 행 목록에서 작업일자 → DAILY_CD 맵 */
function BuildDailyCdByWorkDateFromDetailRows(detailRows) {
  const map = new Map();
  for (const r of detailRows || []) {
    const ymd = PickPermitWorkDateYmd(r);
    const dc = ResolveDetailDailyCd(r);
    if (ymd && dc) map.set(ymd, dc);
  }
  return map;
}

/**
 * 삭제 대상 — 작업일자(YYYY-MM-DD)별 1건, 해당 일자의 DAILY_CD로 API 호출 (모바일 DeletePerNewD1)
 */
function GroupDetailDeletesByWorkDate(rows, allDetailRows) {
  const map = new Map();
  const pool = [...(allDetailRows || []), ...(rows || [])];
  const dailyCdByWorkDate = BuildDailyCdByWorkDateFromDetailRows(pool);

  for (const row of rows || []) {
    const ymd = PickPermitWorkDateYmd(row);
    if (!ymd || map.has(ymd)) continue;

    const dailyCd = String(
      dailyCdByWorkDate.get(ymd) ||
        ResolveDetailDailyCd(
          pool.find((r) => PickPermitWorkDateYmd(r) === ymd) || row
        ) ||
        ''
    ).trim();
    if (!dailyCd) continue;

    map.set(ymd, {
      workDate: ymd,
      dailyCd,
      pmCd: ResolvePmCdForPermitSave(row),
    });
  }
  return [...map.values()];
}

/** 펼친 세부 그리드의 전체 행 (마스터 detailRows 또는 AG Grid 노드) */
function CollectAllDetailRowsForPmCd(pmCd, rowData1, detailApisRef) {
  const key = String(pmCd ?? '').trim();
  if (!key) return [];
  const fromMaster = FindMasterDetailRows(key, rowData1);
  if (fromMaster.length > 0) return fromMaster;
  const collected = [];
  const api = detailApisRef?.current?.get?.(key);
  api?.forEachNode?.((node) => {
    if (node?.data) collected.push(node.data);
  });
  return collected;
}

/**
 * SavePerNewU3 payload 행 생성 (반려·승인)
 * 상세 그리드 선택 시 overrideDtlCds 기준으로 dailyCd/workDate/dtlCd 매핑
 */
function BuildSavePerNewU3PayloadRows({
  masterRows,
  rowData1,
  gubun,
  overrideDtlCds = [],
  returnDetail = '',
  centerId = '',
  centerSignPath = '',
  smSignPath = '',
}) {
  const dtlList = (overrideDtlCds || [])
    .map((v) => String(v ?? '').trim())
    .filter((v) => v !== '');

  if (dtlList.length > 0) {
    const master = masterRows?.[0];
    const pmCd = ResolvePmCdForPermitSave(master);
    const detailRows = FindMasterDetailRows(pmCd, rowData1);
    return dtlList.map((dtlCd) => {
      const det =
        detailRows.find(
          (d) =>
            String(d?.dtlCd ?? d?.DtlCd ?? d?.DTL_CD ?? '').trim() === dtlCd
        ) ?? null;
      const base = det || master;
      return {
        pmCd,
        gubun,
        returnDetail,
        dtlCd,
        workDate: PickPermitWorkDateYmd(base),
        dailyCd: PickPermitDailyCd(base),
        centerId,
        centerSignPath,
        smSignPath,
      };
    });
  }

  return (masterRows || []).map((item) => ({
    pmCd: ResolvePmCdForPermitSave(item),
    gubun,
    returnDetail,
    dtlCd:
      String(item?.dtlCd ?? item?.DtlCd ?? item?.DTL_CD ?? '').trim() || '0',
    workDate: PickPermitWorkDateYmd(item),
    dailyCd: PickPermitDailyCd(item),
    centerId,
    centerSignPath,
    smSignPath,
  }));
}

/** TB_BS_CODE GROUP_CD=WORK_TYPE — CODE_NM → 레포트 필드명 (첨부 코드표와 동일) */
const WORK_TYPE_CODE_NM_TO_WT = {
  일반작업: 'WT_001',
  화기작업: 'WT_002',
  밀폐공간작업: 'WT_003',
  전기작업: 'WT_004',
  고소작업: 'WT_005',
  굴착작업: 'WT_006',
  잠수작업: 'WT_007',
};

/** TB_BS_CODE CODE — headerData workTypeCd{n} 등 */
const WORK_TYPE_WT_TO_CD = {
  WT_001: 'CD_WT_001',
  WT_002: 'CD_WT_002',
  WT_003: 'CD_WT_003',
  WT_004: 'CD_WT_004',
  WT_005: 'CD_WT_005',
  WT_006: 'CD_WT_006',
  WT_007: 'CD_WT_007',
};

/** GetPerNewR3 MST 행: camelCase / PascalCase / UPPER_SNAKE 키 허용 */
function PickDetail(row, ...keys) {
  if (row == null || typeof row !== 'object') return '';
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(row, k)) continue;
    const v = row[k];
    if (v !== undefined && v !== null) return v;
  }
  return '';
}

function AsPerNewArray(v) {
  if (v == null) return [];
  return Array.isArray(v) ? v : [];
}

/**
 * GetPerNewR3: Dailies = 요청 WorkDate 당일만, DailiesByPmCd = PM_CD 전체 작업일+Detail/Item.
 * 우측 그리드·PDF는 전 일자 상세가 필요하므로 DailiesByPmCd 우선.
 */
function MergePerNewDailiesForEnv(row) {
  if (!row || typeof row !== 'object') return [];
  const byPmCd = AsPerNewArray(
    row.dailiesByPmCd ?? row.DailiesByPmCd ?? row.DAILIES_BY_PM_CD
  );
  if (byPmCd.length > 0) return byPmCd;
  return AsPerNewArray(row.dailies ?? row.Dailies);
}

/** GetPerNewR3 페이로드: Mst, Dailies(당일), DailiesByPmCd(전체), CenterSigns(당일 DailyCd) */
function NormalizePerNewR3Payload(row) {
  if (!row || typeof row !== 'object') {
    return {
      mst: null,
      dailies: [],
      dailiesByPmCd: [],
      centerSigns: [],
    };
  }
  return {
    mst: row.mst ?? row.Mst ?? null,
    dailies: AsPerNewArray(row.dailies ?? row.Dailies),
    dailiesByPmCd: AsPerNewArray(
      row.dailiesByPmCd ?? row.DailiesByPmCd ?? row.DAILIES_BY_PM_CD
    ),
    dailiesForView: MergePerNewDailiesForEnv(row),
    centerSigns: AsPerNewArray(row.centerSigns ?? row.CenterSigns),
  };
}

function FlattenPerNewDetails(dailies) {
  const out = [];
  for (const daily of AsPerNewArray(dailies)) {
    const details = AsPerNewArray(daily?.details ?? daily?.Details);
    const dailyWorkDate = daily?.workDate ?? daily?.WorkDate ?? '';
    const dailyCd = daily?.dailyCd ?? daily?.DailyCd ?? '';
    /** GetPerNewR3_Daily CASE AS Status — 동일 일자(DAILY) 내 세부 행 공통 */
    const listStatus = daily?.status ?? daily?.Status ?? '';
    const dailyDepSign = daily?.depSign ?? daily?.DepSign ?? '';
    const dailySmSign = daily?.smSign ?? daily?.SmSign ?? '';
    const dailyReturnYn = daily?.returnYn ?? daily?.ReturnYn ?? '';
    for (const det of details) {
      const detWorkDate = det?.workDate ?? det?.WorkDate ?? '';
      out.push({
        ...det,
        workDate: detWorkDate || dailyWorkDate,
        /** 일자(DAILY) 단위 — 부모 DAILY의 DAILY_CD·WORK_DATE 사용 (DETAIL에 잘못된 DAILY_CD가 있어도 일자별 삭제 보장) */
        dailyCd,
        listStatus,
        status: listStatus,
        depSign: dailyDepSign,
        smSign: dailySmSign,
        returnYn: dailyReturnYn,
      });
    }
  }
  return out;
}

/** Detail StrTime/EndTime — HH:mm 정규화 (목록 그리드 padHm과 동일) */
function PadHmPerNewTime(t) {
  if (t == null || t === '') return '';
  const parts = String(t).split(':');
  const h = parts[0] ?? '00';
  const m = parts[1] ?? '00';
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** detail 작업일시 종료: EndTime만 (연장 ExtEndTime 미사용) */
function PickDetailEndTimeForWork(d) {
  return d?.endTime ?? '';
}

/** detail 연장 종료: ExtEndTime만. 없으면 빈값(EndTime으로 채우지 않음) */
function PickDetailEndTimeForExtension(d) {
  const ext = d?.extEndTime ?? '';
  if (ext == null || String(ext).trim() === '') return '';
  return ext;
}

/**
 * 작업종류 슬라이스 detail에서 StrTime 최소,
 * 작업 종료(EndTime만) 최대, 연장 종료(ExtEndTime 있는 행만) 최대
 */
function AggregateDetailStrAndEndTimes(details) {
  let minStr = '';
  let maxEndWork = '';
  let maxEndExt = '';
  for (const d of details) {
    const st = d?.strTime;
    const enWork = PickDetailEndTimeForWork(d);
    const enExt = PickDetailEndTimeForExtension(d);
    const stP = PadHmPerNewTime(st) || String(st ?? '').trim();
    const enWorkP = PadHmPerNewTime(enWork) || String(enWork ?? '').trim();
    const enExtP = PadHmPerNewTime(enExt) || String(enExt ?? '').trim();
    if (stP) {
      if (!minStr || stP.localeCompare(minStr) < 0) minStr = stP;
    }
    if (enWorkP) {
      if (!maxEndWork || enWorkP.localeCompare(maxEndWork) > 0) {
        maxEndWork = enWorkP;
      }
    }
    if (enExtP) {
      if (!maxEndExt || enExtP.localeCompare(maxEndExt) > 0) {
        maxEndExt = enExtP;
      }
    }
  }
  return { strTime: minStr, endTime: maxEndWork, extEndTime: maxEndExt };
}

/** daily의 workDate + detail 시각 → 레포트용 `YYYY-MM-DD HH:mm` */
function FormatPerNewDetailYmdHm(dateRaw, hmRaw) {
  const hm = PadHmPerNewTime(hmRaw) || String(hmRaw ?? '').trim();
  if (dateRaw == null || dateRaw === '') {
    return hm || '';
  }
  const d = dayjs(dateRaw);
  const ymd = d.isValid() ? d.format('YYYY-MM-DD') : '';
  if (ymd && hm) return `${ymd} ${hm}`;
  if (ymd) return ymd;
  return hm || '';
}

/**
 * dailies(또는 dailiesByPmCd 병합) 기준 각 detail 행:
 * (workDate+strTime) ~ (workDate+endTime), 복수 행은 ", "로 이어 붙임.
 */
function BuildWorkDateTimeTextFromDetails(detailsSlice) {
  const segments = [];
  for (const d of detailsSlice) {
    const wd = d.workDate ?? '';
    const st = d.strTime ?? '';
    const en = PickDetailEndTimeForWork(d);
    const start = FormatPerNewDetailYmdHm(wd, st);
    const end = FormatPerNewDetailYmdHm(wd, en);
    if (start && end) segments.push(`${start} ~ ${end}`);
    else if (start) segments.push(start);
    else if (end) segments.push(end);
  }
  return segments.join(', ');
}

/** ExtEndTime 없는 행은 연장 구간에 포함하지 않음 */
function BuildExtensionDateTimeTextFromDetails(detailsSlice) {
  const segments = [];
  for (const d of detailsSlice) {
    const en = PickDetailEndTimeForExtension(d);
    if (!en || String(en).trim() === '') continue;
    const wd = d.workDate ?? '';
    const st = d.strTime ?? '';
    const start = FormatPerNewDetailYmdHm(wd, st);
    const end = FormatPerNewDetailYmdHm(wd, en);
    if (start && end) segments.push(`${start} ~ ${end}`);
    else if (end) segments.push(end);
  }
  return segments.join(', ');
}

/** detail에서 연장 안전관리자 성명·서명경로 — 슬라이스 내 첫 비어 있지 않은 값 */
function PickDetailExtSmFields(details) {
  let extSmNm = '';
  let extSmSignPath = '';
  for (const d of details) {
    const nm = d?.extSmNm ?? '';
    const path = d?.extSmSignPath ?? '';
    const nmT = String(nm ?? '').trim();
    const pathT = String(path ?? '').trim();
    if (!extSmNm && nmT) extSmNm = nmT;
    if (!extSmSignPath && pathT) extSmSignPath = pathT;
    if (extSmNm && extSmSignPath) break;
  }
  return { extSmNm, extSmSignPath };
}

/** detail 행 기타비고 — 조회 컬럼명(camel/snake/대문자) 병기 */
function PickDetailEtcRemark(d) {
  if (!d || typeof d !== 'object') return '';
  const v =
    d.etcRemark ??
    d.EtcRemark ??
    d.etc_remark ??
    d.ETC_REMARK ??
    d.Etc_Remark ??
    '';
  const s = v != null ? String(v).trim() : '';
  return s;
}

/** TB_BO_PERMIT_ITEM_* ItemCheck: 위험/장비 등 확인(0) 등 */
function PerNewItemCheckConfirmed(v) {
  return v === '0' || v === 0 || v === true;
}

/** 준비사항 prepItems ItemCheck: '0'=필요(필요 열), '1'=필요 아님(확인 열) */
function PrepItemCheckIsNeed(v) {
  return v === '0' || v === 0;
}

function PrepItemCheckIsNotNeed(v) {
  return v === '1' || v === 1;
}

/** 준비사항 prepItems WitnessCheck: 레포트 '확인' 열 — `0`만 체크, `1` 등은 미체크 */
function PrepWitnessCheckConfirmed(v) {
  if (v === undefined || v === null || v === '') return false;
  if (v === 0 || v === '0') return true;
  const s = String(v).trim();
  return s === '0';
}

/** 레포트용 WitnessCheck 코드 문자열 (`"0"` / `"1"` / 기타·없음 `""`) */
function PrepWitnessCheckReportCode(v) {
  if (v === undefined || v === null || String(v).trim() === '') return '';
  if (v === 0 || v === 1) return v === 0 ? '0' : '1';
  const s = String(v).trim();
  if (s === '0' || s === '1') return s;
  return s;
}

/** Telerik 등 `Fields.PREP_CONFIRMED = "true"` 문자열 비교용 */
function PrepConfirmedReportString(confirmed) {
  return confirmed ? 'true' : 'false';
}

function MergePerNewRiskItems(details) {
  const byCode = new Map();
  for (const det of details) {
    const risks = AsPerNewArray(det?.riskItems ?? det?.RiskItems);
    for (const ri of risks) {
      const code = String(ri?.riskCode ?? '').trim();
      if (!code) continue;
      const name = ri?.riskNm ?? '';
      const checked = PerNewItemCheckConfirmed(ri?.itemCheck ?? ri?.ItemCheck);
      const prev = byCode.get(code);
      if (!prev) {
        byCode.set(code, { code, name, checked });
      } else {
        byCode.set(code, {
          code,
          name: prev.name || name,
          checked: prev.checked || checked,
        });
      }
    }
  }
  return [...byCode.values()].sort((a, b) =>
    String(a.code).localeCompare(String(b.code))
  );
}

function MergePerNewEquipItems(details) {
  const byCode = new Map();
  for (const det of details) {
    const items = AsPerNewArray(det?.equipItems ?? det?.EquipItems);
    for (const it of items) {
      const code = String(it?.workEquipCode ?? '').trim();
      if (!code) continue;
      const name = it?.workEquipNm ?? '';
      const checked = PerNewItemCheckConfirmed(it?.itemCheck ?? it?.ItemCheck);
      const prev = byCode.get(code);
      if (!prev) {
        byCode.set(code, { code, name, checked });
      } else {
        byCode.set(code, {
          code,
          name: prev.name || name,
          checked: prev.checked || checked,
        });
      }
    }
  }
  return [...byCode.values()].sort((a, b) =>
    String(a.code).localeCompare(String(b.code))
  );
}

function MergePerNewPrepItems(details) {
  const byCode = new Map();
  for (const det of details) {
    const items = AsPerNewArray(det?.prepItems ?? det?.PrepItems);
    for (const it of items) {
      const code = String(it?.preparationCode ?? '').trim();
      if (!code) continue;
      const name = it?.preparationNm ?? '';
      const v = it?.itemCheck ?? it?.ItemCheck;
      const isNeed = PrepItemCheckIsNeed(v);
      const isNotNeed = PrepItemCheckIsNotNeed(v);
      const w = it?.witnessCheck ?? it?.WitnessCheck;
      const prev = byCode.get(code);
      const need = prev ? prev.need || isNeed : isNeed;
      const notNeed = prev ? prev.notNeed || isNotNeed : isNotNeed;
      const hasW = w !== undefined && w !== null && String(w).trim() !== '';
      byCode.set(code, {
        code,
        name: prev?.name || name,
        need,
        notNeed,
        /** 선택된 준비사항 목록 등 — 필요('0') 여부 */
        checked: need,
        itemCheckRaw:
          v !== undefined && v !== null && String(v) !== ''
            ? v
            : prev?.itemCheckRaw,
        /** 병합 후 한 번만 판정(OR 금지: 한 줄은 1인데 다른 줄 0이면 잘못 체크됨) */
        witnessCheckRaw: hasW ? w : prev?.witnessCheckRaw,
      });
    }
  }
  const merged = [...byCode.values()].map((e) => ({
    ...e,
    witnessConfirmed: PrepWitnessCheckConfirmed(e.witnessCheckRaw),
  }));
  return merged.sort((a, b) => String(a.code).localeCompare(String(b.code)));
}

/** 레포트 유해위험요인·안전보호구 고정 슬롯 수 (Fields.RISK_n_*, EQUIP_n_*) */
const REPORT_RISK_EQUIP_SLOT_COUNT = 18;

function ExtractPerNewR2Catalog(apiResponse) {
  if (apiResponse == null) return null;
  let head = null;
  if (Array.isArray(apiResponse)) {
    head =
      apiResponse.length > 0 && typeof apiResponse[0] === 'object'
        ? apiResponse[0]
        : null;
  } else if (typeof apiResponse === 'object') {
    const ok =
      apiResponse.isSuccess !== false && apiResponse.IsSuccess !== false;
    if (!ok) return null;
    const data = apiResponse.data ?? apiResponse.Data;
    head = Array.isArray(data) && data.length > 0 ? data[0] : null;
  }
  if (!head || typeof head !== 'object') return null;
  return {
    workTypes: AsPerNewArray(head.workTypes ?? head.WorkTypes),
    riskCodes: AsPerNewArray(head.riskCodes ?? head.RiskCodes),
    equipCodes: AsPerNewArray(head.equipCodes ?? head.EquipCodes),
    preparationCodes: AsPerNewArray(
      head.preparationCodes ?? head.PreparationCodes
    ),
  };
}

function FindWorkItemIdForSlice(sliceNm, workTypes) {
  const nm = String(sliceNm ?? '').trim();
  if (!nm || !Array.isArray(workTypes)) return '';
  const hit = workTypes.find((w) => {
    const wnm = String(w.workTypeNm ?? w.WorkTypeNm ?? '').trim();
    return wnm === nm;
  });
  return String(hit?.workItemId ?? hit?.WorkItemId ?? '').trim();
}

function BuildRiskSlotsFromMasterAndDetail(masterRisks, mergedByCode) {
  const sorted = [...masterRisks].sort((a, b) =>
    String(a.riskCode ?? a.RiskCode ?? '').localeCompare(
      String(b.riskCode ?? b.RiskCode ?? '')
    )
  );
  const row = {};
  for (let i = 1; i <= REPORT_RISK_EQUIP_SLOT_COUNT; i++) {
    const it = sorted[i - 1];
    const code = it ? String(it.riskCode ?? it.RiskCode ?? '').trim() : '';
    const name = it ? String(it.riskNm ?? it.RiskNm ?? '').trim() : '';
    const m = code && mergedByCode ? mergedByCode.get(code) : null;
    row[`RISK_${i}_CODE`] = code;
    row[`RISK_${i}_NAME`] = name;
    row[`RISK_${i}_CHECKED`] = !!(m && m.checked);
  }
  return row;
}

function BuildEquipSlotsFromMasterAndDetail(masterEquips, mergedByCode) {
  const sorted = [...masterEquips].sort((a, b) =>
    String(a.workEquipCode ?? a.WorkEquipCode ?? '').localeCompare(
      String(b.workEquipCode ?? b.WorkEquipCode ?? '')
    )
  );
  const row = {};
  for (let i = 1; i <= REPORT_RISK_EQUIP_SLOT_COUNT; i++) {
    const it = sorted[i - 1];
    const code = it
      ? String(it.workEquipCode ?? it.WorkEquipCode ?? '').trim()
      : '';
    const name = it
      ? String(it.workEquipNm ?? it.WorkEquipNm ?? '').trim()
      : '';
    const m = code && mergedByCode ? mergedByCode.get(code) : null;
    row[`EQUIP_${i}_CODE`] = code;
    row[`EQUIP_${i}_NAME`] = name;
    row[`EQUIP_${i}_CHECKED`] = !!(m && m.checked);
  }
  return row;
}

function BuildPreparationTableFromMasterAndDetail(
  masterPreps,
  prepMergedByCode
) {
  const sorted = [...masterPreps].sort((a, b) =>
    String(a.preparationCode ?? a.PreparationCode ?? '').localeCompare(
      String(b.preparationCode ?? b.PreparationCode ?? '')
    )
  );
  return sorted.map((p) => {
    const code = String(p.preparationCode ?? p.PreparationCode ?? '').trim();
    const name = String(p.preparationNm ?? p.PreparationNm ?? '').trim();
    const m = code ? prepMergedByCode.get(code) : null;
    return {
      PREP_CODE: code,
      PREP_NAME: name,
      PREP_NEED: !!(m && m.need),
      PREP_NOT_NEED: !!(m && m.notNeed),
      /** '확인' 열: WitnessCheck — 0만 체크 (레포트는 문자열 "true"/"false") */
      PREP_CONFIRMED: PrepConfirmedReportString(!!(m && m.witnessConfirmed)),
      PREP_WITNESS_CHECK: m
        ? PrepWitnessCheckReportCode(m.witnessCheckRaw)
        : '',
      PREP_CHECKED: !!(m && m.need),
      WORKER_NAME: '',
    };
  });
}

function BuildRiskSlotsFallbackFromMergedOnly(mergedList) {
  const row = {};
  for (let i = 1; i <= REPORT_RISK_EQUIP_SLOT_COUNT; i++) {
    row[`RISK_${i}_CODE`] = '';
    row[`RISK_${i}_NAME`] = '';
    row[`RISK_${i}_CHECKED`] = false;
  }
  mergedList.slice(0, REPORT_RISK_EQUIP_SLOT_COUNT).forEach((item, index) => {
    const fieldIndex = index + 1;
    row[`RISK_${fieldIndex}_CHECKED`] = item.checked;
    row[`RISK_${fieldIndex}_NAME`] = item.name || '';
    row[`RISK_${fieldIndex}_CODE`] = item.code || '';
  });
  return row;
}

function BuildEquipSlotsFallbackFromMergedOnly(mergedList) {
  const row = {};
  for (let i = 1; i <= REPORT_RISK_EQUIP_SLOT_COUNT; i++) {
    row[`EQUIP_${i}_CODE`] = '';
    row[`EQUIP_${i}_NAME`] = '';
    row[`EQUIP_${i}_CHECKED`] = false;
  }
  mergedList.slice(0, REPORT_RISK_EQUIP_SLOT_COUNT).forEach((item, index) => {
    const fieldIndex = index + 1;
    row[`EQUIP_${fieldIndex}_CHECKED`] = item.checked;
    row[`EQUIP_${fieldIndex}_NAME`] = item.name || '';
    row[`EQUIP_${fieldIndex}_CODE`] = item.code || '';
  });
  return row;
}

/** Detail의 WorkTypeNm 기준으로 해당 작업종류 행만 (종류별 1장 레포트용) */
function FilterPerNewDetailsByWorkTypeNm(detailRows, workTypeNm) {
  if (workTypeNm == null || String(workTypeNm).trim() === '') {
    return detailRows;
  }
  const target = String(workTypeNm).trim();
  return detailRows.filter((d) => String(d.workTypeNm ?? '').trim() === target);
}

function SAM_BASIC_02F(props) {
  //#region 변수
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { userInfo, lanInfo } = useSelector((state) => state.BaseInfo);

  //로그 관련
  const { SaveActionLog } = useActionLog();

  const gridRef1 = useRef(); // 마스터 그리드
  const gridRef4 = useRef(); // 반려이력 그리드
  /** GetPerNewR3 성공 시 env·reportData 및 인쇄용 executePermitPdf 보관 */
  const perNewPrintCacheRef = useRef(null);
  /** GetPerNewR2 — 작업종별 위험/보호구/준비 마스터(레포트 전체 슬롯) */
  const perNewR2CatalogRef = useRef(null);
  const perNewR2CatalogCustRef = useRef('');
  /** 동일 허가서(pmCd+workDate) 재클릭 시 GetPerNewR3·GetReports 생략 */
  const detailPreviewCacheKeyRef = useRef('');
  const search2InFlightRef = useRef('');
  const lastPreviewBlobUrlRef = useRef('');
  const executePermitPdfRef = useRef(null);
  const tableDataRef = useRef([]); // 레포트 - 작업종류조회
  const tableData2 = useRef([]); // 레포트 - 작업별상세조회

  const [rowData1, setRowData1] = useState([]); //그리드 데이터
  /** 목록(GetSamBasic02R4) 재조회 시 마스터 행 전부 접기 — onFirstDataRendered는 최초 1회만 호출됨 */
  const [masterListRefreshKey, setMasterListRefreshKey] = useState(0);
  const [detailTitle2, setDetailTitle2] = useState('작업 세부내역');
  const [selectedRowData1, setSelectedRowData1] = useState([]);
  const [permitPreviewLoading, setPermitPreviewLoading] = useState(false);
  const detailApisRef = useRef(new Map());
  const activePmCdRef = useRef('');
  /** 목록 행 펼침 유지용 (상세 조회 후 rowData 갱신 시 접힘 방지) */
  const expandedPmCdRef = useRef('');
  const resetDetailHeightTimerRef = useRef(null);

  const RestoreMasterExpanded = useCallback((pmCdKey) => {
    const key = String(pmCdKey ?? '').trim();
    if (!key) return;
    expandedPmCdRef.current = key;
    window.requestAnimationFrame(() => {
      const masterApi = gridRef1.current?.api;
      if (!masterApi) return;
      masterApi.forEachNode((node) => {
        const rPm = String(
          node.data?.pmCd ?? node.data?.PmCd ?? node.data?.PM_CD ?? ''
        ).trim();
        node.setExpanded(rPm === key);
      });
    });
  }, []);

  const ScheduleMasterDetailHeightReset = useCallback((pmCdKey, detailCount) => {
    if (resetDetailHeightTimerRef.current) {
      clearTimeout(resetDetailHeightTimerRef.current);
    }
    resetDetailHeightTimerRef.current = window.setTimeout(() => {
      const masterApi = gridRef1.current?.api;
      if (!masterApi) {
        resetDetailHeightTimerRef.current = null;
        return;
      }
      const pmCd = String(
        pmCdKey ?? expandedPmCdRef.current ?? ''
      ).trim();
      if (pmCd && detailCount != null) {
        applyExpandedDetailRowHeight(masterApi, pmCd, detailCount);
      } else if (pmCd) {
        let count = 0;
        masterApi.forEachNode((node) => {
          if (node.detail) return;
          const rPm = String(
            node.data?.pmCd ?? node.data?.PmCd ?? node.data?.PM_CD ?? ''
          ).trim();
          if (rPm === pmCd) {
            count = node.data?.detailRows?.length ?? 0;
          }
        });
        applyExpandedDetailRowHeight(masterApi, pmCd, count);
      } else {
        masterApi.resetRowHeights?.();
      }
      window.requestAnimationFrame(() => {
        masterApi.resetRowHeights?.();
      });
      resetDetailHeightTimerRef.current = null;
    }, 150);
  }, []);
  const [changedRowData1, setChangedRowData1] = useState([]);
  const [deletedRowData1, setDeletedRowData1] = useState([]);
  const [clickRowId, setClickRowId] = useState(null);
  const selectedRowNosRef1 = useRef(new Set());
  const lastCheckedIndexRef1 = useRef(null);
  const currentRowIndexRef1 = useRef(null);

  const [modalToggle1, setModalToggle1] = useState(false); // 반려사유팝업
  const [rejectReason, setRejectReason] = useState(''); // 반려사유
  const [rejectTargetRows, setRejectTargetRows] = useState([]); // 반려 대상 행(행 버튼 클릭 시, SaveSamBasic02U2와 동일 행 기준)
  /** 상세 그리드에서 반려/승인 시 선택된 DTL_CD들 (모달 확인 시 사용) */
  const rejectDtlCdsRef = useRef([]);

  const [modalToggle2, setModalToggle2] = useState(false); // 서명팝업(결재승인자/안전관리자 승인 시)
  const [approvalTargetRows, setApprovalTargetRows] = useState([]); // 승인 대상 행(모달 확인 시 사용)
  const [pendingApprovalGubun, setPendingApprovalGubun] = useState(''); // 모달에서 확인 시 사용할 Gubun ('1' 또는 '2')
  /** 상세 그리드에서 승인 시 선택된 DTL_CD들 (모달 확인 시 사용) */
  const approveDtlCdsRef = useRef([]);
  const [signInputs, setSignInputs] = useState({
    mapUrl: '',
    mapFiles: {},
  }); // 서명 state
  const signatureCanvas = useRef(); // 서명 REF

  const [modalToggle3, setModalToggle3] = useState(false); // 반려이력팝업
  const [modalRowData3, setModalRowData3] = useState([]); // 반려이력팝업그리드
  const [selectedModalRowData3, setSelectedModalRowData3] = useState([]); // 안전보건관리책임자 평가표 '평가표 목록' 버튼, 팝업 창

  const [codeManagementOpen, setCodeManagementOpen] = useState(false); // 코드관리 팝업
  const [deleteMasterConfirmOpen, setDeleteMasterConfirmOpen] = useState(false);
  const [deleteMasterConfirmCount, setDeleteMasterConfirmCount] = useState(0);

  // QUERY: 조회/저장/삭제 훅
  const searchInfo = useSelector((state) => state.SearchInfo);

  // context를 useMemo로 동적으로 계산
  // ref는 dependency 배열에 포함하지 않음 (ref.current의 값들을 직접 dependency로 사용)
  const searchRefCurrent = props.searchRef?.current;
  const context = useMemo(
    () => ({
      search: {
        custCd: searchInfo.custCd || searchRefCurrent?.custCd || '',
        depCd: searchInfo.depCd || searchRefCurrent?.depCd || '',
        workStartDate:
          searchInfo.startDate || searchRefCurrent?.startDate || '',
        workEndDate: searchInfo.endDate || searchRefCurrent?.endDate || '',
        gubun: searchInfo.gubun || searchRefCurrent?.gubun || '',
        searchText: searchInfo.searchText || searchRefCurrent?.searchText || '',
      },
      userInfo,
    }),
    [
      searchInfo.custCd,
      searchInfo.depCd,
      searchInfo.startDate,
      searchInfo.endDate,
      searchInfo.gubun,
      searchInfo.searchText,
      searchRefCurrent?.custCd,
      searchRefCurrent?.depCd,
      searchRefCurrent?.startDate,
      searchRefCurrent?.endDate,
      searchRefCurrent?.gubun,
      searchRefCurrent?.searchText,
      userInfo,
    ]
  );

  /** GetSamBasic02R4 (PM_CD 1행) → 그리드 행 */
  const FormatSearch1RowsAndSetGrid = useCallback((rows) => {
    const padHm = (t) => {
      if (t == null || t === '') return '';
      const parts = String(t).split(':');
      const h = parts[0] ?? '00';
      const m = parts[1] ?? '00';
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };
    const formattedData = (rows || []).map((item) => {
      const workDateRaw = item.workDate ?? '';
      const ymd = workDateRaw
        ? dayjs(workDateRaw).isValid()
          ? dayjs(workDateRaw).format('YYYY-MM-DD')
          : ''
        : '';
      const totalStartRaw = item.totalStartDate ?? '';
      const totalEndRaw = item.totalEndDate ?? '';
      const toYmd = (raw) => {
        if (raw == null || raw === '') return '';
        if (typeof raw === 'number' && Number.isFinite(raw)) {
          const d = dayjs(raw);
          return d.isValid() ? d.format('YYYY-MM-DD') : '';
        }
        const s = String(raw).trim();
        if (!s) return '';
        const d = dayjs(s);
        if (d.isValid()) return d.format('YYYY-MM-DD');
        const t = Date.parse(s);
        if (!Number.isNaN(t)) return dayjs(t).format('YYYY-MM-DD');
        return '';
      };
      const totalStartYmd = toYmd(totalStartRaw);
      const totalEndYmd = toYmd(totalEndRaw);
      const createDate = toYmd(
        item.createDate ?? item.CreateDate ?? item.CREATE_DATE ?? ''
      );
      const strRaw = item.strTime ?? '';
      const endRaw = item.endTime ?? '';
      const strP = padHm(strRaw) || (strRaw != null ? String(strRaw) : '');
      const endP = padHm(endRaw) || (endRaw != null ? String(endRaw) : '');

      return {
        pmCd: item.pmCd ?? '',
        reqId: item.reqId ?? item.ReqId ?? item.REQ_ID ?? '',
        reqNm: item.reqNm ?? '',
        createDate,
        totalStartDate: totalStartYmd || ymd,
        totalEndDate: totalEndYmd || ymd,
        partnerNm: item.partnerNm ?? '',
        workContent: item.workContent ?? '',
        strTime: strP,
        endTime: endP,
        workItems: item.workItems ?? '',
        status: item.status ?? '',
        workDate: ymd,
        depCd: item.depCd ?? '',
        smId: item.smId ?? '',
        currentUserDepSigned: item.currentUserDepSigned ?? '',
        signedDepSignCount: item.signedDepSignCount ?? '',
        totalDepSignCount: item.totalDepSignCount ?? '',
        depSign: item.depSign ?? '',
        smSign: item.smSign ?? '',
        returnYn: item.returnYn ?? '',
      };
    });

    setRowData1(Commons.InsertNumber(formattedData));
    setMasterListRefreshKey((k) => k + 1);
    detailPreviewCacheKeyRef.current = '';
    perNewPrintCacheRef.current = null;
  }, []);

  const { refetch: Search1 } = useSearch1({
    context: context,
    onSuccess: FormatSearch1RowsAndSetGrid,
  });

  const { runAsync: Save2Async } = useSave2();
  const { runAsync: Save3Async } = useSave3();
  const delete1 = useDelete1();
  const delete2 = useDelete2();

  // 삭제 Handler(useDeleteN)
  const deleteHandler1 = useDelete({
    gridRef: gridRef1,
    deleteMutation: delete1,
    updateDeleted: setDeletedRowData1,
    updateRowData: setRowData1,
    deletedRowData: deletedRowData1,
    rowData: rowData1,
    setChangedRowData: setChangedRowData1,
  });

  const updateCurrentRowData = (fieldName, value, skipValidation = false) => {
    if (!gridRef1.current || currentRowIndexRef1.current === null) return;

    const api = gridRef1.current.api;
    const rowNode = api.getDisplayedRowAtIndex(currentRowIndexRef1.current);
    if (!rowNode) return;

    const prevData = rowNode.data;
    const updatedRowData = {
      ...prevData,
      [fieldName]: value,
      editYn: 'Y',
    };

    rowNode.setData(updatedRowData);

    if (
      !skipValidation &&
      (!updatedRowData.DEP_NM1 || !updatedRowData.DEP_NM1.trim())
    ) {
      return;
    }

    if (!Array.isArray(changedRowData1)) return;

    const alreadyChangedIndex = changedRowData1.findIndex(
      (item) => item.no === updatedRowData.no
    );
    const newChangedList = [...changedRowData1];

    if (alreadyChangedIndex > -1) {
      newChangedList[alreadyChangedIndex] = updatedRowData;
    } else {
      newChangedList.push(updatedRowData);
    }
    setChangedRowData1(changedRowData1);
  };
  //#endregion

  //#region useEffect
  useEffect(() => {
    if (!props.searchClick2) return;
    ResetLogic1();

    if (!props.validateSelection()) {
      props.setSearchClick2(false);
      return;
    }

    SearchLogic1();
    props.setSearchClick2(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.searchClick2]);

  // 처리상태(gubun) 변경 시 자동 재조회
  const prevGubunRef = React.useRef(
    searchInfo.gubun || searchRefCurrent?.gubun || ''
  );
  useEffect(() => {
    const currentGubun = searchInfo.gubun || searchRefCurrent?.gubun || '';

    // gubun이 변경되지 않았으면 실행하지 않음
    if (prevGubunRef.current === currentGubun) return;

    // searchClick2가 이미 처리 중이면 중복 실행 방지
    if (props.searchClick2) {
      prevGubunRef.current = currentGubun;
      return;
    }

    if (!props.validateSelection()) {
      prevGubunRef.current = currentGubun;
      return;
    }

    // gubun이 변경되었을 때만 조회
    prevGubunRef.current = currentGubun;
    SearchLogic1();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInfo.gubun, searchRefCurrent?.gubun]);

  // 버튼 로직
  useEffect(() => {
    if (props.filterClick2) {
      if (!props.validateSelection()) {
        props.setFilterClick2(false);
        return;
      }

      gridRef1.current.api.setQuickFilter(
        props.searchRef?.current?.searchText || ''
      );
      props.setFilterClick2(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterClick2]);

  /** 작업허가서 탭 인쇄: 펼쳐진 세부내역 그리드 선택 행만 PDF */
  useEffect(() => {
    if (!props.printClick2) return;

    const RunPrintSelection = async () => {
      props.setPrintClick2(false);

      if (!props.validateSelection()) {
        return;
      }

      const exec = executePermitPdfRef.current;
      const cache = perNewPrintCacheRef.current;
      if (!exec || !cache?.env) {
        dispatch(
          showSnackbar({
            message:
              '목록에서 허가서를 선택해 조회한 뒤, 우측 그리드에서 인쇄할 작업세부내역을 선택하세요.',
            type: 'warning',
          })
        );
        return;
      }

      const activePmCd =
        activePmCdRef.current ||
        String(
          selectedRowData1?.[0]?.pmCd ??
            selectedRowData1?.[0]?.PmCd ??
            selectedRowData1?.[0]?.PM_CD ??
            ''
        );
      const api = activePmCd ? detailApisRef.current.get(activePmCd) : null;
      const selected = api?.getSelectedRows?.() ?? [];
      if (selected.length === 0) {
        dispatch(
          showSnackbar({
            message:
              '작업세부내역(목록 행 아래 펼쳐진 그리드)에서 인쇄할 행을 선택하세요.',
            type: 'warning',
          })
        );
        return;
      }

      const full = FlattenPerNewDetails(
        cache.env.dailiesForView ?? cache.env.dailies
      );
      const dtlSet = new Set();
      for (const r of selected) {
        const list = r?.dtlCdList ?? r?.DtlCdList;
        if (Array.isArray(list) && list.length > 0) {
          for (const v of list) {
            const t = String(v ?? '').trim();
            if (t) dtlSet.add(t);
          }
          continue;
        }
        const one = String(r?.dtlCd ?? r?.DtlCd ?? '').trim();
        if (one) dtlSet.add(one);
      }
      if (dtlSet.size === 0) {
        dispatch(
          showSnackbar({
            message: '선택한 행에 상세키가 없습니다.',
            type: 'warning',
          })
        );
        return;
      }

      const pool = full.filter((d) =>
        dtlSet.has(String(d.dtlCd ?? d.DtlCd ?? '').trim())
      );
      if (pool.length === 0) {
        dispatch(
          showSnackbar({
            message: '선택한 항목의 상세 데이터를 찾을 수 없습니다.',
            type: 'warning',
          })
        );
        return;
      }

      await exec(pool, {
        mode: 'selection',
        selectedRows: selected,
        attachPreviewToMaster: false,
        openPdfWindow: true,
      });
    };

    void RunPrintSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.printClick2]);
  //#endregion

  const RegisterDetailApi = (pmCd, api) => {
    if (!pmCd || !api) return;
    detailApisRef.current.set(String(pmCd), api);
  };
  const SetActivePmCd = (pmCd) => {
    activePmCdRef.current = pmCd ? String(pmCd) : '';
  };

  //#region 조회
  //안전작업허가서 검색 버튼 이벤트
  const SearchLogic1 = async () => {
    setSignInputs({
      mapUrl: '',
      mapFiles: {},
    });
    ResetLogic1();

    try {
      const result = await Search1();
      // refetch 직후 useEffect(onSuccess)가 타지 않는 경우가 있어, 응답으로 그리드 직접 반영
      if (result?.isSuccess && result.data != null) {
        handleQuerySuccess(result.data, {
          onSuccess: (rows) => FormatSearch1RowsAndSetGrid(rows),
        });
      }
    } catch (error) {
      // 에러는 onError에서 처리
    }
  };
  //#endregion

  // 우측 미리보기
  const SearchLogic2 = async (_PM_CD) => {
    setLoading(true);

    try {
      await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Execute.execute,
        [
          {
            procedureName: 'SP_PERMIT_W_R2',
            _PM_CD: Commons.EncryptAES(_PM_CD) || '',
          },
        ]
      ).then((result) => {
        let data = result.data;

        if (data !== 'Error') {
          tableDataRef.current = data;
        }
      });
    } catch (e) {
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  // 우측 미리보기
  const SearchLogic3 = async (_PM_CD) => {
    setLoading(true);

    try {
      await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Execute.execute,
        [
          {
            procedureName: 'SP_PERMIT_W_R4',
            _PM_CD: Commons.EncryptAES(_PM_CD) || '',
          },
        ]
      ).then((result) => {
        let data = result.data;

        if (data !== 'Error') {
          tableData2.current = data;
        }
      });
    } catch (e) {
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  // 반려이력 조회
  const { runAsync: Search3 } = useSearch3({
    onSuccess: async (rows) => {
      if (!rows || rows.length === 0) {
        dispatch(
          showSnackbar({
            message: '반려이력이 없습니다.',
            type: 'info',
          })
        );
        setModalRowData3([]);
        return;
      }
      // GetSamBasic02R3: MST + RETURN 평면 row 형태(대문자/PascalCase) → 그리드용 camelCase 정규화
      const normalized = (rows || []).map((r) => ({
        ...r,
        pmCd: r.pmCd ?? '',
        returnId: r.returnId ?? '',
        returnNm: r.returnNm ?? '',
        returnDate: r.returnDate ?? '',
        returnDetail: r.returnDetail ?? '',
        dtlCd: r.dtlCd ?? '',
      }));
      setModalRowData3(normalized);
    },
  });

  const { run: Search4 } = useSearch4({
    onSuccess: async (rows) => {
      console.log(rows);
    },
  });
  const fetchManagers = () => {
    return new Promise((resolve) => {
      Search4({
        onSuccess: (rows) => {
          resolve(rows);
        },
      });
    });
  };
  const SearchReturn = async (_PM_CD) => {
    if (!_PM_CD) return;
    setModalRowData3([]);
    setSelectedModalRowData3([]);
    await Search3({
      row: { pmCd: _PM_CD },
    });
  };

  //#region 저장
  /** 펼쳐진 작업세부내역 그리드에서 선택된 행 */
  const GetDetailSelectedRows = useCallback(() => {
    const pmCd =
      activePmCdRef.current ||
      String(
        selectedRowData1?.[0]?.pmCd ??
          selectedRowData1?.[0]?.PmCd ??
          selectedRowData1?.[0]?.PM_CD ??
          ''
      ).trim();
    if (!pmCd) return { pmCd: '', rows: [] };
    const api = detailApisRef.current.get(pmCd);
    return { pmCd, rows: api?.getSelectedRows?.() ?? [] };
  }, [selectedRowData1]);

  /** 상세(DTL_CD) 1건 이상 삭제 — DeletePerNewD2 */
  const DeleteDetailLogic = async () => {
    const { pmCd, rows } = GetDetailSelectedRows();
    if (!rows.length) {
      dispatch(
        showSnackbar({
          message:
            '목록 행을 펼친 뒤 작업세부내역 그리드에서 삭제할 행을 선택하세요.',
          type: 'warning',
        })
      );
      return;
    }

    const toDelete = rows.filter((r) => PickPermitWorkDateYmd(r));
    if (toDelete.length === 0) {
      dispatch(
        showSnackbar({
          message: '선택한 행에 작업일자가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }

    try {
      const allDetailRows = CollectAllDetailRowsForPmCd(
        pmCd,
        rowData1,
        detailApisRef
      );
      const selectedDayKeys = new Set(
        toDelete.map((r) => ResolveDetailDayKey(r)).filter(Boolean)
      );
      const expandedToDelete =
        selectedDayKeys.size > 0
          ? allDetailRows.filter((r) =>
              selectedDayKeys.has(ResolveDetailDayKey(r))
            )
          : toDelete;

      const dailyDeleteTargets = GroupDetailDeletesByWorkDate(
        expandedToDelete,
        allDetailRows
      );

      if (dailyDeleteTargets.length === 0) {
        dispatch(
          showSnackbar({
            message:
              '선택한 작업일자에 해당하는 DAILY_CD를 찾을 수 없습니다. 상세를 다시 조회하세요.',
            type: 'warning',
          })
        );
        return;
      }

      for (const target of dailyDeleteTargets) {
        await delete2.runAsync([
          {
            dailyCd: target.dailyCd,
            pmCd: target.pmCd || pmCd,
            workDate: target.workDate,
            dtlCd: '',
          },
        ]);
      }

      dispatch(
        showSnackbar({
          message: '선택한 작업세부내역이 삭제되었습니다.',
          type: 'success',
        })
      );

      await SaveActionLog(expandedToDelete, {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'SUCCESS',
      });

      const detailApi = detailApisRef.current.get(pmCd);
      detailApi?.deselectAll?.();

      detailPreviewCacheKeyRef.current = '';
      perNewPrintCacheRef.current = null;
      search2InFlightRef.current = '';

      // 마스터 목록(GetSamBasic02R4) 재조회 — 세부 삭제 후 목록·기간 등 반영
      await SearchLogic1();
    } catch (e) {
      await SaveActionLog(toDelete, {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'ERROR',
        errorMsg: e.message,
      });
      dispatch(
        showSnackbar({
          message: e.message || '상세 삭제 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  /** 상세 그리드 선택행의 dtlCd 배열 */
  const GetSelectedDetailDtlCds = useCallback(() => {
    const { pmCd, rows } = GetDetailSelectedRows();
    const dtlCds = (rows || [])
      .map((r) => String(r?.dtlCd ?? r?.DtlCd ?? r?.DTL_CD ?? '').trim())
      .filter((v) => v !== '');
    return { pmCd, dtlCds };
  }, [GetDetailSelectedRows]);

  /** 상세(작업종류) 행 단위 반려: 반려사유 모달 오픈 */
  const TriggerDetailReject = useCallback(() => {
    const { pmCd, dtlCds } = GetSelectedDetailDtlCds();
    if (!pmCd || dtlCds.length === 0) {
      dispatch(
        showSnackbar({
          message:
            '작업세부내역(목록 행 아래 펼쳐진 그리드)에서 반려할 행을 선택하세요.',
          type: 'warning',
        })
      );
      return;
    }

    const masterRow =
      (rowData1 || []).find(
        (r) => String(r.pmCd ?? r.PmCd ?? r.PM_CD ?? '').trim() === pmCd
      ) ||
      selectedRowData1?.[0] ||
      { pmCd };

    const { rows } = GetDetailSelectedRows();
    if (!canDetailApproveReject(rows, masterRow, userInfo)) {
      dispatch(
        showSnackbar({
          message:
            rows?.length > 1
              ? '선택한 행 중 결재요청·승인요청·연장요청 상태가 아니거나, 현재 권한으로 처리할 수 없습니다.'
              : '결재요청·승인요청·연장요청 상태에서만 반려할 수 있습니다.',
          type: 'warning',
        })
      );
      return;
    }

    rejectDtlCdsRef.current = dtlCds;
    setRejectTargetRows([masterRow]);
    setRejectReason('');
    setModalToggle1(true);
  }, [
    GetSelectedDetailDtlCds,
    GetDetailSelectedRows,
    dispatch,
    rowData1,
    selectedRowData1,
    showSnackbar,
    userInfo,
  ]);

  /** 상세(작업종류) 행 단위 승인: 서명 모달 오픈 */
  const TriggerDetailApprove = useCallback(() => {
    const { pmCd, rows } = GetDetailSelectedRows();
    if (!pmCd || !rows?.length) {
      dispatch(
        showSnackbar({
          message:
            '작업세부내역(목록 행 아래 펼쳐진 그리드)에서 승인할 행을 선택하세요.',
          type: 'warning',
        })
      );
      return;
    }

    const masterRow =
      (rowData1 || []).find(
        (r) => String(r.pmCd ?? r.PmCd ?? r.PM_CD ?? '').trim() === pmCd
      ) ||
      selectedRowData1?.[0] ||
      { pmCd };

    const gubun = getApprovalGubunForDetailRows(rows, masterRow, userInfo);
    if (!gubun) {
      dispatch(
        showSnackbar({
          message:
            rows.length > 1
              ? '선택한 행의 결재 단계가 같지 않거나, 현재 권한으로 승인할 수 없는 상태입니다.'
              : '결재요청·승인요청·연장요청(요청) 상태에서만 승인할 수 있습니다.',
          type: 'warning',
        })
      );
      return;
    }

    const dtlCds = rows
      .map((r) => String(r?.dtlCd ?? r?.DtlCd ?? r?.DTL_CD ?? '').trim())
      .filter((v) => v !== '');
    approveDtlCdsRef.current = dtlCds;
    setPendingApprovalGubun(gubun);
    setApprovalTargetRows([masterRow]);
    setSignInputs({ mapUrl: '', mapFiles: {} });
    setModalToggle2(true);
  }, [
    GetDetailSelectedRows,
    dispatch,
    rowData1,
    selectedRowData1,
    showSnackbar,
    userInfo,
  ]);

  /** 목록에서 체크 선택한 작업허가서 */
  const GetMasterRowsToDelete = useCallback(() => {
    const api = gridRef1.current?.api;
    const selected =
      api?.getSelectedRows?.() ??
      (Array.isArray(selectedRowData1) ? selectedRowData1 : []);
    return selected.filter((r) => {
      const pm = String(r?.pmCd ?? r?.PmCd ?? r?.PM_CD ?? '').trim();
      return pm && r?.newYn !== 'Y';
    });
  }, [selectedRowData1]);

  /** [허가서 전체 삭제] 클릭 — 확인 후 삭제 */
  const OpenDeleteMasterPermitConfirm = () => {
    const rowsToDelete = GetMasterRowsToDelete();
    if (rowsToDelete.length === 0) {
      dispatch(
        showSnackbar({
          message:
            '삭제할 작업허가서를 목록에서 체크한 뒤 [허가서 전체 삭제]를 누르세요.',
          type: 'warning',
        })
      );
      return;
    }
    setDeleteMasterConfirmCount(rowsToDelete.length);
    setDeleteMasterConfirmOpen(true);
  };

  /** 작업허가서 전체 삭제 — DeleteSamBasic02D1 (PM_CD) */
  const DeleteMasterLogic = async () => {
    setDeleteMasterConfirmOpen(false);

    const rowsToDelete = GetMasterRowsToDelete();
    if (rowsToDelete.length === 0) {
      dispatch(
        showSnackbar({
          message:
            '삭제할 작업허가서를 목록에서 체크한 뒤 [허가서 전체 삭제]를 누르세요.',
          type: 'warning',
        })
      );
      return;
    }

    try {
      for (const row of rowsToDelete) {
        await delete1.runAsync([row]);
      }

      dispatch(
        showSnackbar({
          message: '작업허가서가 삭제되었습니다.',
          type: 'success',
        })
      );

      await SaveActionLog(rowsToDelete, {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'SUCCESS',
      });

      setDeletedRowData1([]);
      const api = gridRef1.current?.api;
      if (api) {
        const remain = (rowData1 || []).filter(
          (row) =>
            !rowsToDelete.some((del) => del.no === row.no)
        );
        setRowData1(remain);
      }
      SearchLogic1();
    } catch (e) {
      await SaveActionLog(rowsToDelete, {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'ERROR',
        errorMsg: e.message,
      });
      dispatch(
        showSnackbar({
          message: e.message || '작업허가서 삭제 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  // 반려 처리 함수
  const RejectLogic = async (reason) => {
    const rowsToReject =
      rejectTargetRows?.length > 0 ? rejectTargetRows : selectedRowData1;
    const overrideDtlCds = Array.isArray(rejectDtlCdsRef.current)
      ? rejectDtlCdsRef.current.filter((v) => String(v ?? '').trim() !== '')
      : [];
    try {
      if (!rowsToReject || rowsToReject.length === 0) {
        dispatch(
          showSnackbar({
            message: '반려할 항목을 선택해주세요.',
            type: 'warning',
          })
        );
        return;
      }

      const context = {
        userInfo: userInfo,
      };

      // 연장신청(연장요청) 반려는 MST RETURN_YN이 아니라 DETAIL의 EXT_RETURN_* 갱신이 필요
      // → SavePerNewU6(Gubun=3)로 처리
      const activePmForReject =
        activePmCdRef.current ||
        String(
          rowsToReject?.[0]?.pmCd ??
            rowsToReject?.[0]?.PmCd ??
            rowsToReject?.[0]?.PM_CD ??
            ''
        ).trim();
      const rejectDetailPool =
        overrideDtlCds.length > 0 && activePmForReject
          ? (FindMasterDetailRows(activePmForReject, rowData1) || []).filter(
              (d) =>
                overrideDtlCds
                  .map((v) => String(v).trim())
                  .includes(String(d?.dtlCd ?? d?.DtlCd ?? '').trim())
            )
          : [];
      const hasExtensionReject =
        rejectDetailPool.length > 0
          ? rejectDetailPool.some(
              (d) =>
                String(
                  d?.listStatus ?? d?.status ?? d?.ListStatus ?? ''
                ).trim() === '연장요청'
            )
          : rowsToReject.some((r) => {
              const { status } = getPermitStatusAndButton(r, userInfo || {});
              return status === '연장요청';
            });

      if (hasExtensionReject) {
        const activePmCd =
          activePmCdRef.current ||
          String(
            rowsToReject?.[0]?.pmCd ??
              rowsToReject?.[0]?.PmCd ??
              rowsToReject?.[0]?.PM_CD ??
              ''
          );
        const detailRows =
          (activePmCd
            ? rowData1.find(
                (r) =>
                  String(r?.pmCd ?? r?.PmCd ?? r?.PM_CD ?? '') === activePmCd
              )?.detailRows
            : null) ?? [];
        let pendingDtlCds = (detailRows || [])
          .filter((d) => {
            const isExt = String(d?.isExtended ?? '').toUpperCase() === 'Y';
            const smYn = String(d?.extSmYn ?? '').toUpperCase();
            const retYn = String(d?.extReturnYn ?? '').toUpperCase();
            return isExt && smYn !== 'Y' && retYn !== 'Y';
          })
          .map((d) => String(d?.dtlCd ?? '').trim())
          .filter((v) => v !== '');
        if (overrideDtlCds.length > 0) {
          const set = new Set(overrideDtlCds.map((v) => String(v).trim()));
          pendingDtlCds = pendingDtlCds.filter((cd) => set.has(String(cd)));
        }

        if (pendingDtlCds.length === 0) {
          dispatch(
            showSnackbar({
              message:
                '연장 반려 대상 상세(DTL_CD)를 찾을 수 없습니다. 상세 조회 후 다시 시도하세요.',
              type: 'warning',
            })
          );
          return;
        }

        await Save3Async([
          {
            userInfo,
            gubun: '3',
            smSignPath: '',
            returnReason: reason || '',
            dtlCds: pendingDtlCds,
          },
        ]);
      } else {
        const saveRows = BuildSavePerNewU3PayloadRows({
          masterRows: rowsToReject,
          rowData1,
          gubun: '0',
          overrideDtlCds,
          returnDetail: reason || '',
        });

        await Save2Async(saveRows, context);
      }

      dispatch(
        showSnackbar({
          message: '반려 되었습니다.',
          type: 'success',
        })
      );

      setRejectTargetRows([]);
      rejectDtlCdsRef.current = [];

      // 정상 완료 시 로그
      await SaveActionLog([...rowsToReject], {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'SUCCESS',
      });

      await SignRequest('0', rowsToReject);
      SearchLogic1(); // 다시 조회
    } catch (e) {
      setRejectTargetRows([]);
      rejectDtlCdsRef.current = [];
      // 오류 시 로그
      await SaveActionLog([...rowsToReject], {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'ERROR',
        errorMsg: e.message,
      });

      console.error(e);
      dispatch(
        showSnackbar({
          message: e.message || '오류가 발생했습니다.',
          type: e.code === 'CUSTOM_ERROR' ? e.type : 'error',
        })
      );
    }
  };

  // 승인 처리 함수 (gubun: '1' 결재승인자, '2' 안전관리자 / rowsOverride: 지정 시 해당 행만 승인)
  const ApproveLogic = async (gubun, sign, rowsOverride) => {
    const rowsToApprove = rowsOverride ?? selectedRowData1;
    const overrideDtlCds = Array.isArray(approveDtlCdsRef.current)
      ? approveDtlCdsRef.current.filter((v) => String(v ?? '').trim() !== '')
      : [];
    try {
      if (!rowsToApprove || rowsToApprove.length === 0) {
        dispatch(
          showSnackbar({
            message: '승인할 항목을 선택해주세요.',
            type: 'warning',
          })
        );
        return;
      }

      const context = {
        userInfo: userInfo,
      };

      const signMapFile = signInputs.mapFiles;
      const hasSignFile =
        signMapFile instanceof File &&
        typeof signMapFile.size === 'number' &&
        signMapFile.size > 0;

      // 서명 파일 저장 (중간결재 Gubun 1 · 최종 Gubun 2)
      if ((gubun === '1' || gubun === '2') && hasSignFile) {
        await SaveLogic3(signMapFile);
      }

      const signFilePath =
        hasSignFile && signMapFile.name
          ? `Business/${props.searchRef?.current?.custCd || ''}/SAF_BASIC_02/${Commons.DecryptoAES(userInfo.userid)}/${signMapFile.name}`
          : '';
      const centerIdPlain =
        Commons.DecryptoAES(userInfo.userid || userInfo.userId || '') || '';

      const saveRows = BuildSavePerNewU3PayloadRows({
        masterRows: rowsToApprove,
        rowData1,
        gubun,
        overrideDtlCds,
        returnDetail: '',
        centerId: gubun === '1' ? centerIdPlain : '',
        centerSignPath: gubun === '1' ? signFilePath : '',
        smSignPath: gubun === '2' ? signFilePath : '',
      });
      // 연장요청(연장 승인) 건은 MST(SavePerNewU3) 대신 DETAIL EXT_SM_*(SavePerNewU6)로 처리해야 상태가 변경됨.
      const activePmForApprove =
        activePmCdRef.current ||
        String(
          rowsToApprove?.[0]?.pmCd ??
            rowsToApprove?.[0]?.PmCd ??
            rowsToApprove?.[0]?.PM_CD ??
            ''
        ).trim();
      const approveDetailPool =
        overrideDtlCds.length > 0 && activePmForApprove
          ? (FindMasterDetailRows(activePmForApprove, rowData1) || []).filter(
              (d) =>
                overrideDtlCds
                  .map((v) => String(v).trim())
                  .includes(String(d?.dtlCd ?? d?.DtlCd ?? '').trim())
            )
          : [];
      const hasExtensionApproval =
        gubun === '2' &&
        (approveDetailPool.length > 0
          ? approveDetailPool.some(
              (d) =>
                String(
                  d?.listStatus ?? d?.status ?? d?.ListStatus ?? ''
                ).trim() === '연장요청'
            )
          : rowsToApprove.some((r) => {
              const { status } = getPermitStatusAndButton(r, userInfo || {});
              return status === '연장요청';
            }));

      if (hasExtensionApproval) {
        const activePmCd =
          activePmCdRef.current ||
          String(
            rowsToApprove?.[0]?.pmCd ??
              rowsToApprove?.[0]?.PmCd ??
              rowsToApprove?.[0]?.PM_CD ??
              ''
          );
        const detailRows =
          (activePmCd
            ? rowData1.find(
                (r) =>
                  String(r?.pmCd ?? r?.PmCd ?? r?.PM_CD ?? '') === activePmCd
              )?.detailRows
            : null) ?? [];
        let pendingDtlCds = (detailRows || [])
          .filter((d) => {
            const isExt =
              String(d?.isExtended ?? '').toUpperCase() === 'Y' ||
              String(d?.IsExtended ?? '').toUpperCase() === 'Y';
            const smYn = String(d?.extSmYn ?? '').toUpperCase();
            const retYn = String(d?.extReturnYn ?? '').toUpperCase();
            return isExt && smYn !== 'Y' && retYn !== 'Y';
          })
          .map((d) => String(d?.dtlCd ?? '').trim())
          .filter((v) => v !== '');
        if (overrideDtlCds.length > 0) {
          const set = new Set(overrideDtlCds.map((v) => String(v).trim()));
          pendingDtlCds = pendingDtlCds.filter((cd) => set.has(String(cd)));
        }

        if (pendingDtlCds.length === 0) {
          dispatch(
            showSnackbar({
              message:
                '연장 승인 대상 상세(DTL_CD)를 찾을 수 없습니다. 상세 조회 후 다시 시도하세요.',
              type: 'warning',
            })
          );
          return;
        }

        await Save3Async([
          {
            userInfo,
            gubun: '2',
            smSignPath: signFilePath,
            returnReason: '',
            dtlCds: pendingDtlCds,
          },
        ]);
      } else {
        await Save2Async(saveRows, context);
      }

      if (gubun === '1') {
        dispatch(
          showSnackbar({
            message: '중간결재(센터) 서명이 완료되었습니다.',
            type: 'success',
          })
        );
        await SearchLogic1();
      } else if (gubun === '2') {
        dispatch(
          showSnackbar({
            message: '최종 결재(안전관리자) 승인이 완료되었습니다.',
            type: 'success',
          })
        );
        await SearchLogic1();
      }

      // DB 저장 완료 직후 동기로 패드 닫기 (이후 SaveActionLog·SignRequest 지연/실패 시에도 UI는 닫힘)
      flushSync(() => {
        setModalToggle2(false);
        setApprovalTargetRows([]);
        setPendingApprovalGubun('');
      });
      approveDtlCdsRef.current = [];
      signatureCanvas.current?.clear?.();
      setSignInputs({ mapUrl: '', mapFiles: {} });

      await SaveActionLog([...rowsToApprove], {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'SUCCESS',
      });

      await SignRequest(gubun, rowsToApprove);

      await SearchLogic1();
    } catch (e) {
      approveDtlCdsRef.current = [];
      // 오류 시 로그
      await SaveActionLog([...rowsToApprove], {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'ERROR',
        errorMsg: e.message,
      });

      console.error(e);
      dispatch(
        showSnackbar({
          message: e.message || '오류가 발생했습니다.',
          type: e.code === 'CUSTOM_ERROR' ? e.type : 'error',
        })
      );
    }
  };

  // 서명 저장
  const SaveLogic3 = async (_value1) => {
    try {
      let formList = new FormData();
      let filePath = `Business/${props.searchRef?.current?.custCd || ''}/SAF_BASIC_02/${Commons.DecryptoAES(userInfo.userid)}`;

      formList.append('UserId', userInfo.userid);
      formList.append('FilePath', filePath); // 저장경로(파일명 제외)
      formList.append('FormFile', _value1); // 파일

      const result = await Commons.Axios(
        'file',
        Global.file.useUrl + Global.urls.Execute.videoUpload,
        null,
        formList
      );
      const data = result.data;

      if (data?.filePath) {
      } else {
        dispatch(
          showSnackbar({
            message: '파일 업로드 실패 또는 파일 경로 없음',
            type: 'error',
          })
        );
        return null;
      }
    } catch (e) {
    } finally {
    }
  };

  // 파일 저장
  const SaveLogic2 = async (item) => {
    setLoading(true);
    try {
      const payload = {
        oldFilePath: item.filePath || '', // 기존 파일 경로 (없으면 빈 문자열)
        sourcePreviewPath: item.PREVIEW_FILE_PATH, // Preview 파일 경로
        newFileName: item.saveName, // 최종 저장할 파일명
        targetPath: `SafeWorkProcedure/${props.searchRef?.current?.custCd || ''}/SAF_BASIC_02`,
      };

      const { data } = await Commons.Axios(
        'post1',
        Global.file.useUrl + Global.urls.Execute.moveAndReplaceFile,
        payload
      );

      if (!data || data.type === 'Error') {
        throw new Error(data?.message || '파일 이동 실패');
      }

      return data; // 성공 시 서버 응답 반환
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e.message || '파일 저장 실패',
          type: 'error',
        })
      );
      console.error('SaveLogic2 Error:', e);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // 미리보기 파일 저장 - GridFileUploadF 공통 함수에서 사용
  const SaveLogicPreview = async (_file) => {
    setLoading(true);

    const MAX_CHUNK_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB
    const filePath = 'PreviewFiles';
    let finalSavePath = '';

    try {
      // ✅ 대용량 파일: 청크 업로드
      if (_file.size > MAX_CHUNK_UPLOAD_SIZE) {
        finalSavePath = await uploadInChunks(_file);
      }
      // ✅ 일반 파일 업로드
      else {
        let formList = new FormData();
        formList.append('UserId', userInfo.userid);
        formList.append('FilePath', filePath); // 저장 경로
        formList.append('FormFile', _file); // 파일

        const result = await Commons.Axios(
          'file2',
          Global.file.useUrl + Global.urls.Execute.videoUpload,
          null,
          formList
        );

        finalSavePath = result?.data?.filePath || '';
      }

      // ✅ 후처리: 저장 경로 정리 및 HWP 변환
      if (finalSavePath) {
        const savePath = finalSavePath
          .replace(/^.*\\PreviewFiles/, 'PreviewFiles')
          .replace(/\\/g, '/');

        const ext = _file.name.split('.').pop()?.toLowerCase();
        if (ext === 'hwp') {
          const hwpUrl = await ConvertHwpToPdf(savePath);
          return hwpUrl || null;
        }

        return savePath;
      } else {
        dispatch(
          showSnackbar({
            message: '파일 업로드 실패 또는 파일 경로 없음',
            type: 'error',
          })
        );
        return null;
      }
    } catch (e) {
      console.error('Error in SaveLogicPreview:', e);
      dispatch(
        showSnackbar({
          message: '파일 업로드 중 오류 발생',
          type: 'error',
        })
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const uploadInChunks = async (file, chunkSize = 5 * 1024 * 1024) => {
    const totalChunks = Math.ceil(file.size / chunkSize);
    const filePath = 'PreviewFiles';
    const saveName = file.name;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('chunkIndex', i);
      formData.append('totalChunks', totalChunks);
      formData.append('fileName', saveName);
      formData.append('UserId', userInfo.userid);
      formData.append('FilePath', filePath);

      await Commons.Axios(
        'chunk',
        Global.file.useUrl + Global.urls.Execute.UploadChunk,
        null,
        formData
      );
    }

    // 병합 요청
    const mergeResponse = await Commons.Axios(
      'post',
      Global.file.useUrl + Global.urls.Execute.MergeChunks,
      null, // 쿼리스트링 없음
      {
        fileName: saveName,
        filePath,
        userId: userInfo.userid,
      }
    );

    return mergeResponse?.data?.filePath || '';
  };

  // HWP -> PDF 변환
  const ConvertHwpToPdf = async (_filePath) => {
    if (!_filePath) {
      console.error('ConvertHwpToPdf: filePath가 없습니다.');
      return null;
    }

    try {
      const payload = { FilePath: _filePath };

      const response = await Commons.Axios(
        'post1',
        Global.file.useUrl + Global.urls.Execute.ConvertHwpToPdf,
        payload
      );

      // 서버 응답 검증
      if (response?.data?.url) {
        return response.data.url;
        // 서버가 '/PreviewFiles/xxx.pdf' 형태로 반환하면 'files/PreviewFiles/xxx.pdf'
      } else {
        console.error('ConvertHwpToPdf: 서버 응답이 유효하지 않음', response);
        return null;
      }
    } catch (error) {
      console.error('ConvertHwpToPdf: 요청 중 오류 발생', error);
      return null;
    }
  };
  //#endregion

  //#region 나머지 함수

  const SignRequest = async (_GUBUN, rowsOverride) => {
    const rows =
      Array.isArray(rowsOverride) && rowsOverride.length > 0
        ? rowsOverride
        : selectedRowData1;
    try {
      const item = rows[0];
      if (!item) {
        return;
      }

      let saveData = [];

      if (_GUBUN === '0') {
        rows.forEach((item) => {
          const row = {
            responseId: Commons.EncryptAES(item.userId),
            title: Commons.EncryptAES('작업허가서'),
            body: Commons.EncryptAES('작업허가서 반려 안내'),
            gubun: Commons.EncryptAES('AL_002_002'),
            fcmToken: Commons.EncryptAES(''),
            requestId: userInfo.userid,
            route: Commons.EncryptAES(`/M_Permit`),
            responseData: Commons.EncryptAES(
              item.pmCd || item.PM_CD || item.PmCd || ''
            ),
          };
          saveData.push(row);
        });
      } else if (_GUBUN === '1') {
        const managers = await fetchManagers();

        const data = managers || [];

        for (const manager of data) {
          if (manager.id) {
            const row = {
              responseId: Commons.EncryptAES(manager.id),
              title: Commons.EncryptAES('작업허가서'),
              body: Commons.EncryptAES('작업 허가서 요청입니다.'),
              fcmToken: Commons.EncryptAES(''),
              gubun: Commons.EncryptAES('AL_002_001'),
              requestId: userInfo.userid,
              route: Commons.EncryptAES(`/M_Permit`),
              responseData: Commons.EncryptAES(
                item.pmCd || item.PM_CD || item.PmCd || ''
              ),
            };
            saveData.push(row);
          }
        }
      } else if (_GUBUN === '2') {
        rows.forEach((item) => {
          const row = {
            responseId: Commons.EncryptAES(item.userId),
            title: Commons.EncryptAES('작업허가서'),
            body: Commons.EncryptAES('작업허가서 승인이 완료되었습니다.'),
            fcmToken: Commons.EncryptAES(''),
            gubun: Commons.EncryptAES('AL_002_003'),
            requestId: userInfo.userid,
            route: Commons.EncryptAES(`/M_Permit`),
            responseData: Commons.EncryptAES(
              item.pmCd || item.PM_CD || item.PmCd || ''
            ),
          };
          saveData.push(row);
        });
      }

      const result = await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.FCM.fcm,
        saveData
      );

      const responseData = result.data;

      if (Array.isArray(responseData) && responseData.length > 0) {
        if (responseData[0].type === 'Error') {
          dispatch(
            showSnackbar({ message: responseData[0].message, type: 'error' })
          );
        } else {
          dispatch(
            showSnackbar({ message: '알림이 전송되었습니다.', type: 'success' })
          );
        }
      } else if (responseData === 'Fail') {
        dispatch(
          showSnackbar({
            message: '사용자가 어플에 로그인하지 않았습니다.',
            type: 'warning',
          })
        );
      }
    } catch (error) {
      console.error('🔥 Exception in SignRequest:', error);
      dispatch(
        showSnackbar({
          message: '알림 전송 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  // 서식 다운로드
  const DownloadLogic1 = async () => {
    try {
      await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Execute.ftpResponse,
        {
          value1: Commons.EncryptAES('DocumentForm/작업계획서_예시.hwp'),
        }
      ).then((result) => {
        let data = result.data;

        if (data !== 'Error') {
          // const fileUrl = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data}`;
          // const link = document.createElement('a');
          // link.href = fileUrl;
          // link.download = '작업계획서_예시.hwp';
          // document.body.appendChild(link);
          // link.click();
          // document.body.removeChild(link);

          // Base64 데이터를 Blob으로 변환
          const pdfBlob = new Blob(
            [Uint8Array.from(atob(data), (c) => c.charCodeAt(0))],
            {
              type: 'application/hwp',
            }
          );

          const fileName = '작업계획서_예시.hwp';

          const downloadLink = document.createElement('a');
          const blobUrl = URL.createObjectURL(pdfBlob);
          downloadLink.href = blobUrl;
          downloadLink.download = fileName;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);

          dispatch(
            showSnackbar({
              message: `"${fileName}" 서식 다운로드 완료.`,
              type: 'success',
            })
          );
        }
      });
    } catch (e) {
      console.error(e);
    } finally {
    }
  };

  // 파일 다운로드
  const DownloadLogic2 = async (_data) => {
    if (_data.newYn === 'Y') {
      dispatch(
        showSnackbar({
          message: '저장 후 파일 다운로드가 가능합니다.',
          type: 'warning',
        })
      );
      return;
    }

    if (!_data.filePath) {
      dispatch(
        showSnackbar({ message: '파일이 존재하지 않습니다.', type: 'warning' })
      );
      return;
    }

    try {
      const filePath = fixUrl(_data.filePath);
      const fileName = _data.fileName || 'downloaded_file';

      const params = {
        FileName: fileName,
        Url: filePath,
      };

      await Commons.Axios(
        'fileDown',
        Global.file.useUrl + Global.urls.Execute.UrlFileDownload,
        params
      ).then((result) => {
        const contentType = result?.headers?.['content-type'];
        const blobData = result?.data;

        if (!blobData || !contentType) {
          dispatch(
            showSnackbar({
              message: `"${fileName}" 다운로드 응답이 비정상입니다.`,
              type: 'error',
            })
          );
          return;
        }

        // 1) Blob → Object URL 생성
        const blob = new Blob([blobData], { type: contentType });
        const url = window.URL.createObjectURL(blob);

        // 2) <a> 태그 만들어서 클릭 트리거
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName; // 저장될 파일명
        document.body.appendChild(a);
        a.click();

        // 3) 정리
        a.remove();
        window.URL.revokeObjectURL(url);

        dispatch(
          showSnackbar({
            message: `"${fileName}" 다운로드 완료`,
            type: 'success',
          })
        );
      });
    } catch (e) {
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    }
  };

  // 서명 이미지 파싱(url => base64)
  const ConvertImageToBase64 = async (url) => {
    if (!url) return '';
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result); // Base64 문자열 반환
        };
        reader.onerror = reject; // 오류 처리
        reader.readAsDataURL(blob); // Blob을 Base64로 변환
      });
    } catch (error) {
      console.error('이미지 변환 오류:', error);
      return '';
    }
  };

  const { runAsync: Search5PerNewR2 } = useSearch5({});

  // 상세 조회(GetPerNewR3); 목록 행 클릭 시 우측 미리보기(PDF) 생성
  const BuildDetailPreviewCacheKey = (pmCd, workDate) => {
    const wd = workDate ? String(workDate).trim().substring(0, 10) : '';
    return `${String(pmCd ?? '').trim()}|${wd}`;
  };

  const { runAsync: Search2 } = useSearch2({
    onSuccess: async (rows, response) => {
      try {
        if (!rows || rows.length === 0) {
          setDetailTitle2('작업 세부내역');
          dispatch(
            showSnackbar({
              message: '상세 정보를 조회할 수 없습니다.',
              type: 'warning',
            })
          );
          return;
        }

        const payload = rows[0] ?? {};
        const env = NormalizePerNewR3Payload(payload);
        const rowMst =
          env.mst && typeof env.mst === 'object' && !Array.isArray(env.mst)
            ? env.mst
            : null;

        if (!rowMst) {
          const pmCdHint = String(
            env.dailiesByPmCd?.[0]?.pmCd ??
              env.dailiesForView?.[0]?.pmCd ??
              env.dailies?.[0]?.pmCd ??
              ''
          ).trim();
          const listRow = (rowData1 || []).find(
            (r) => String(r.pmCd ?? r.PmCd ?? r.PM_CD ?? '').trim() === pmCdHint
          );
          const hasDailies =
            (env.dailiesForView?.length ?? 0) > 0 ||
            (env.dailiesByPmCd?.length ?? 0) > 0;

          if (listRow && hasDailies) {
            rowMst = {
              pmCd: listRow.pmCd ?? listRow.PmCd,
              custCd: listRow.custCd ?? listRow.depCd,
              depCd: listRow.depCd,
              reqNm: listRow.reqNm,
              reqId: listRow.reqId ?? listRow.smId,
              totalStartDate: listRow.totalStartDate,
              totalEndDate: listRow.totalEndDate,
              workItems: listRow.workItems,
              workContent: listRow.workContent,
              workLoc: listRow.workLoc,
              partnerNm: listRow.partnerNm,
              partnerManager: listRow.partnerManager,
              partnerTel: listRow.partnerTel,
              workerList: listRow.workerList,
              createDate: listRow.createDate,
            };
          } else {
            setDetailTitle2('작업 세부내역');
            dispatch(
              showSnackbar({
                message:
                  '허가서가 삭제되었거나 상세를 조회할 수 없습니다. 목록을 새로고침하세요.',
                type: 'warning',
              })
            );
            return;
          }
        }

      const custForR2 = String(
        rowMst.custCd ??
          rowMst.CustCd ??
          rowMst.CUST_CD ??
          searchInfo?.custCd ??
          ''
      ).trim();
      if (custForR2) {
        try {
          if (
            perNewR2CatalogCustRef.current !== custForR2 ||
            !perNewR2CatalogRef.current
          ) {
            const r2Raw = await Search5PerNewR2({
              search: { custCd: custForR2 },
            });
            perNewR2CatalogRef.current = ExtractPerNewR2Catalog(r2Raw);
            perNewR2CatalogCustRef.current = custForR2;
          }
        } catch {
          perNewR2CatalogRef.current = null;
          perNewR2CatalogCustRef.current = '';
        }
      }

      const P = (...keys) => PickDetail(rowMst, ...keys);

      const reportData = {
        pmCd: P('pmCd', 'PmCd', 'PM_CD'),
        custCd: P('custCd', 'CustCd', 'CUST_CD'),
        depCd: P('depCd', 'DepCd', 'DEP_CD'),
        reqId: P('reqId', 'ReqId', 'REQ_ID'),
        reqNm: P('reqNm', 'ReqNm', 'REQ_NM'),
        reqDepNm: P('reqDepNm', 'ReqDepNm', 'REQ_DEP_NM'),
        reqRank: P('reqRank', 'ReqRank', 'REQ_RANK'),
        reqPosition: P('reqPosition', 'ReqPosition', 'REQ_POSITION'),
        reqSignPath: P('reqSignPath', 'ReqSignPath', 'REQ_SIGN_PATH'),
        workItems: P('workItems', 'WorkItems', 'WORK_ITEMS'),
        totalStartDate: P(
          'totalStartDate',
          'TotalStartDate',
          'TOTAL_START_DATE'
        ),
        totalEndDate: P('totalEndDate', 'TotalEndDate', 'TOTAL_END_DATE'),
        workLoc: P('workLoc', 'WorkLoc', 'WORK_LOC'),
        workContent: P('workContent', 'WorkContent', 'WORK_CONTENT'),
        workMemberNo: P('workMemberNo', 'WorkMemberNo', 'WORK_MEMBER_NO'),
        partnerNm: P('partnerNm', 'PartnerNm', 'PARTNER_NM'),
        partnerManager: P(
          'partnerManager',
          'PartnerManager',
          'PARTNER_MANAGER'
        ),
        partnerTel: P('partnerTel', 'PartnerTel', 'PARTNER_TEL'),
        workerList: P('workerList', 'WorkerList', 'WORKER_LIST'),
        depSign: P('depSign', 'DepSign', 'DEP_SIGN'),
        smSign: P('smSign', 'SmSign', 'SM_SIGN'),
        smId: P('smId', 'SmId', 'SM_ID'),
        smNm: P('smNm', 'SmNm', 'SM_NM'),
        smDepNm: P('smDepNm', 'SmDepNm', 'SM_DEP_NM'),
        smRank: P('smRank', 'SmRank', 'SM_RANK'),
        smPosition: P('smPosition', 'SmPosition', 'SM_POSITION'),
        smSignPath: P('smSignPath', 'SmSignPath', 'SM_SIGN_PATH'),
        smSignDate: P('smSignDate', 'SmSignDate', 'SM_SIGN_DATE'),
        extSmNm: P('extSmNm', 'ExtSmNm', 'EXT_SM_NM'),
        extSmSignPath: P('extSmSignPath', 'ExtSmSignPath', 'EXT_SM_SIGN_PATH'),
        returnYn: P('returnYn', 'ReturnYn', 'RETURN_YN'),
        returnDetail: P('returnDetail', 'ReturnDetail', 'RETURN_DETAIL'),
        returnId: P('returnId', 'ReturnId', 'RETURN_ID'),
        witnessId: P('witnessId', 'WitnessId', 'WITNESS_ID'),
        witnessYn: P('witnessYn', 'WitnessYn', 'WITNESS_YN'),
        witnessNm: P('witnessNm', 'WitnessNm', 'WITNESS_NM'),
        witnessSignPath: P(
          'witnessSignPath',
          'WitnessSignPath',
          'WITNESS_SIGN_PATH'
        ),
        witnessDepNm: P('witnessDepNm', 'WitnessDepNm', 'WITNESS_DEP_NM'),
        witnessRank: P('witnessRank', 'WitnessRank', 'WITNESS_RANK'),
        witnessPosition: P(
          'witnessPosition',
          'WitnessPosition',
          'WITNESS_POSITION'
        ),
        checkRemark: P('checkRemark', 'CheckRemark', 'CHECK_REMARK'),
        status: P('status', 'Status', 'STATUS'),
        createId: P('createId', 'CreateId', 'CREATE_ID'),
        createDate: P('createDate', 'CreateDate', 'CREATE_DATE'),
        updateId: P('updateId', 'UpdateId', 'UPDATE_ID'),
        updateDate: P('updateDate', 'UpdateDate', 'UPDATE_DATE'),
      };

      for (const [k, v] of Object.entries(rowMst)) {
        if (v === undefined || v === null) continue;
        if (
          /^[a-z][a-zA-Z0-9]*$/.test(k) &&
          !Object.prototype.hasOwnProperty.call(reportData, k)
        ) {
          reportData[k] = v;
          continue;
        }
        if (/^[A-Z][a-z]/.test(k)) {
          const camel = k.charAt(0).toLowerCase() + k.slice(1);
          if (!Object.prototype.hasOwnProperty.call(reportData, camel)) {
            reportData[camel] = v;
          }
        }
      }

      perNewPrintCacheRef.current = { env, reportData };

      const detailRows = FlattenPerNewDetails(env.dailiesForView);
      const dailyCdByWorkDateFromDailies = BuildDailyCdByWorkDateMap(
        env.dailiesForView
      );
      const partnerNmMst =
        reportData.partnerNm ??
        reportData.PartnerNm ??
        reportData.PARTNER_NM ??
        '';
      const partnerManagerMst =
        reportData.partnerManager ??
        reportData.PartnerManager ??
        reportData.PARTNER_MANAGER ??
        '';
      const titleParts = [partnerNmMst, partnerManagerMst].filter(
        (v) => String(v ?? '').trim() !== ''
      );
      setDetailTitle2(
        titleParts.length > 0
          ? `작업 세부내역 - 업체명: ${partnerNmMst || '-'} / 책임자: ${partnerManagerMst || '-'}`
          : '작업 세부내역'
      );
      const detailGridRows = detailRows.map((d) => {
        const wdRaw = d.workDate ?? d.WorkDate ?? '';
        const workDateYmd =
          wdRaw && dayjs(wdRaw).isValid()
            ? dayjs(wdRaw).format('YYYY-MM-DD')
            : '';
        const strRaw = d.strTime ?? d.StrTime;
        const endRaw = PickDetailEndTimeForWork(d);
        const strP =
          PadHmPerNewTime(strRaw) || (strRaw != null ? String(strRaw) : '');
        const endP =
          PadHmPerNewTime(endRaw) || (endRaw != null ? String(endRaw) : '');
        const extEndRaw = PickDetailEndTimeForExtension(d);
        const extEndP =
          extEndRaw != null && String(extEndRaw).trim() !== ''
            ? PadHmPerNewTime(extEndRaw) || String(extEndRaw).trim()
            : '';
        const isExtended =
          d.isExtended ?? d.IsExtended ?? d.IS_EXTENDED ?? d.is_extended ?? '';
        const extSmYn = d.extSmYn ?? d.ExtSmYn ?? d.EXT_SM_YN ?? '';
        const extReturnYn =
          d.extReturnYn ?? d.ExtReturnYn ?? d.EXT_RETURN_YN ?? '';
        const listStatus =
          d.listStatus ??
          d.ListStatus ??
          d.status ??
          d.Status ??
          '';
        return {
          workTypeNm: d.workTypeNm ?? d.WorkTypeNm ?? '',
          partnerNm: partnerNmMst,
          partnerManager: partnerManagerMst,
          workDate: workDateYmd,
          strTime: strP,
          endTime: endP,
          extEndTime: extEndP,
          dtlCd: d.dtlCd ?? d.DtlCd ?? d.DTL_CD ?? '',
          dailyCd:
            dailyCdByWorkDateFromDailies.get(workDateYmd) ||
            String(d.dailyCd ?? d.DailyCd ?? d.DAILY_CD ?? '').trim(),
          workItemId: d.workItemId ?? d.WorkItemId ?? '',
          listStatus,
          status: listStatus,
          smId: reportData.smId ?? reportData.SmId ?? '',
          depSign: d.depSign ?? d.DepSign ?? reportData.depSign ?? '',
          smSign: d.smSign ?? d.SmSign ?? reportData.smSign ?? '',
          returnYn: d.returnYn ?? d.ReturnYn ?? reportData.returnYn ?? '',
          // 연장 승인(U6) 분기를 위해 원본 플래그 보존
          isExtended,
          extSmYn,
          extReturnYn,
        };
      });
      const detailInserted =
        detailGridRows.length > 0 ? Commons.InsertNumber(detailGridRows) : [];
      const pmCdKey = String(reportData.pmCd || '').trim();
      if (pmCdKey) {
        setRowData1((prev) =>
          (prev || []).map((r) => {
            const rPm = String(r.pmCd ?? r.PmCd ?? r.PM_CD ?? '').trim();
            if (rPm !== pmCdKey) return r;
            return {
              ...r,
              detailRows: detailInserted,
              reqId: reportData.reqId ?? r.reqId ?? '',
              createId: reportData.createId ?? r.createId ?? '',
              smId: reportData.smId ?? r.smId ?? '',
              depSign: reportData.depSign ?? r.depSign ?? '',
              smSign: reportData.smSign ?? r.smSign ?? '',
              returnYn: reportData.returnYn ?? r.returnYn ?? '',
            };
          })
        );
        RestoreMasterExpanded(pmCdKey);
        ScheduleMasterDetailHeightReset(pmCdKey, detailInserted.length);
      }

      const executePermitPdf = async (detailPoolRows, options = {}) => {
        const {
          mode = 'full',
          selectedRows = [],
          attachPreviewToMaster = false,
          openPdfWindow = false,
        } = options;

        try {
          const formatDateYmd = (dateValue) => {
            if (!dateValue) return '';
            const formatted = dayjs(dateValue).format('YYYY-MM-DD');
            return formatted === 'Invalid Date' ? '' : formatted;
          };

          let smSignBase64 = null;
          const smPath = String(reportData.smSignPath || '').trim();
          if (smPath) {
            const safetyImageUrl =
              Global.file.useUrl + 'files/' + fixUrl(smPath);
            const base64Image = await ConvertImageToBase64(safetyImageUrl);
            smSignBase64 = base64Image.replace(
              /^data:image\/[a-z]+;base64,/,
              ''
            );
          }

          let reqSignBase64 = null;
          const reqSignPathVal = String(
            reportData.reqSignPath ?? reportData.ReqSignPath ?? ''
          ).trim();
          if (reqSignPathVal) {
            try {
              const reqSignUrl =
                Global.file.useUrl + 'files/' + fixUrl(reqSignPathVal);
              const reqB64 = await ConvertImageToBase64(reqSignUrl);
              reqSignBase64 = reqB64.replace(/^data:image\/[a-z]+;base64,/, '');
            } catch {
              reqSignBase64 = null;
            }
          }

          let witnessSignBase64 = null;
          const witnessSignPathVal = String(
            reportData.witnessSignPath ?? reportData.WitnessSignPath ?? ''
          ).trim();
          if (witnessSignPathVal) {
            try {
              const witnessSignUrl =
                Global.file.useUrl + 'files/' + fixUrl(witnessSignPathVal);
              const witnessB64 = await ConvertImageToBase64(witnessSignUrl);
              witnessSignBase64 = witnessB64.replace(
                /^data:image\/[a-z]+;base64,/,
                ''
              );
            } catch {
              witnessSignBase64 = null;
            }
          }

          const createDate = reportData.createDate || '';
          const permitDate = createDate
            ? dayjs(createDate).format('YYYY-MM-DD')
            : '';

          const SplitCommaTrim = (v) =>
            String(v ?? '')
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0);

          const NormalizeWorkTypeToken = (token) => {
            const t = String(token ?? '').trim();
            if (!t) return '';
            return t.replace(/^CD_/i, '');
          };

          const workItemNames = SplitCommaTrim(reportData.workItems);
          const workTypeCodeTokens = SplitCommaTrim(
            reportData.workTypeCds || reportData.workTypes || ''
          ).map(NormalizeWorkTypeToken);

          const emptyWtFlags = {
            WT_001: false,
            WT_002: false,
            WT_003: false,
            WT_004: false,
            WT_005: false,
            WT_006: false,
            WT_007: false,
          };

          const ResolveWorkTypeWtKey = (nm, i) => {
            const code = workTypeCodeTokens[i];
            const fromToken =
              code && Object.prototype.hasOwnProperty.call(emptyWtFlags, code)
                ? code
                : '';
            const fromNm = WORK_TYPE_CODE_NM_TO_WT[nm] || '';
            return fromToken || fromNm;
          };

          const workTypeNameJoined =
            workItemNames.length > 0
              ? workItemNames.join(' · ')
              : String(reportData.workItems ?? '')
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .join(' · ');

          const centerTableRows = [];
          for (const cs of env.centerSigns) {
            const path = String(
              cs.centerSignPath ?? cs.CenterSignPath ?? ''
            ).trim();
            let centerB64 = null;
            if (path) {
              try {
                const u = Global.file.useUrl + 'files/' + fixUrl(path);
                const rawB64 = await ConvertImageToBase64(u);
                centerB64 = rawB64.replace(/^data:image\/[a-z]+;base64,/, '');
              } catch {
                centerB64 = null;
              }
            }
            centerTableRows.push({
              CENTER_ID: cs.centerId ?? cs.CenterId ?? '',
              CENTER_NAME: cs.centerName ?? cs.CenterName ?? '',
              CENTER_RANK: cs.centerRank ?? cs.CenterRank ?? '',
              CENTER_POSITION: cs.centerPosition ?? cs.CenterPosition ?? '',
              CENTER_DEP_NM: cs.centerDepNm ?? cs.CenterDepNm ?? '',
              CENTER_SIGN_IMAGE: centerB64,
              CENTER_UPDATE_YMD: formatDateYmd(cs.updateDate ?? cs.UpdateDate),
            });
          }

          /** 작업종류별 또는 선택행별 1장씩 PER_NEW 호출 후 pdf-lib로 병합 */
          let slices;
          if (mode === 'selection') {
            slices = (selectedRows || []).map((r, idx) => ({
              key: `row-${idx}`,
              dtlCd: String(r?.dtlCd ?? r?.DtlCd ?? '').trim(),
              workTypeNm: String(r?.workTypeNm ?? r?.WorkTypeNm ?? '').trim(),
            }));
            if (slices.length === 0) {
              dispatch(
                showSnackbar({
                  message: '선택한 행이 없습니다.',
                  type: 'warning',
                })
              );
              return;
            }
          } else {
            slices = workItemNames.length > 0 ? workItemNames : [''];
          }

          const GetSliceKey = (slice) =>
            typeof slice === 'string'
              ? String(slice)
              : String(slice?.key ?? slice?.dtlCd ?? slice?.workTypeNm ?? '');
          const GetSliceNm = (slice) =>
            typeof slice === 'string'
              ? String(slice)
              : String(slice?.workTypeNm ?? '');
          const GetSliceDetails = (slice) => {
            if (mode !== 'selection' || typeof slice === 'string') {
              return FilterPerNewDetailsByWorkTypeNm(detailPoolRows, slice);
            }
            const dtlCd = String(slice?.dtlCd ?? '').trim();
            if (dtlCd) {
              return detailPoolRows.filter(
                (d) => String(d.dtlCd ?? d.DtlCd ?? '').trim() === dtlCd
              );
            }
            return FilterPerNewDetailsByWorkTypeNm(
              detailPoolRows,
              slice?.workTypeNm
            );
          };

          /** MST 작업기간 일자(레포트는 detail StrTime/EndTime과 조합) */
          const mstStartDateYmd = reportData.totalStartDate
            ? formatDateYmd(reportData.totalStartDate)
            : '';
          const mstEndDateYmd = reportData.totalEndDate
            ? formatDateYmd(reportData.totalEndDate)
            : '';

          const extSmSignImageBySlice = {};
          for (const slice of slices) {
            const sliceKey = GetSliceKey(slice);
            const sliceDetailsForExt = GetSliceDetails(slice);
            let extPath =
              PickDetailExtSmFields(sliceDetailsForExt).extSmSignPath;
            extPath = String(extPath ?? '').trim();
            if (!extPath) {
              extPath = String(
                reportData.extSmSignPath ?? reportData.ExtSmSignPath ?? ''
              ).trim();
            }
            if (extPath) {
              try {
                const extUrl = Global.file.useUrl + 'files/' + fixUrl(extPath);
                const rawB64 = await ConvertImageToBase64(extUrl);
                extSmSignImageBySlice[sliceKey] = rawB64.replace(
                  /^data:image\/[a-z]+;base64,/,
                  ''
                );
              } catch {
                extSmSignImageBySlice[sliceKey] = null;
              }
            } else {
              extSmSignImageBySlice[sliceKey] = null;
            }
          }

          const workTypeWitnessSignImageBySlice = {};
          for (const slice of slices) {
            const sliceKey = GetSliceKey(slice);
            const sliceDetailsForWitness = GetSliceDetails(slice);
            let witSignPath = '';
            for (const d of sliceDetailsForWitness) {
              for (const k of [
                'witnessSignPath',
                'WitnessSignPath',
                'WITNESS_SIGN_PATH',
              ]) {
                const v = d[k];
                if (v != null && String(v).trim() !== '') {
                  witSignPath = String(v).trim();
                  break;
                }
              }
              if (witSignPath) break;
            }
            if (witSignPath) {
              try {
                const witUrl =
                  Global.file.useUrl + 'files/' + fixUrl(witSignPath);
                const witB64 = await ConvertImageToBase64(witUrl);
                workTypeWitnessSignImageBySlice[sliceKey] = witB64.replace(
                  /^data:image\/[a-z]+;base64,/,
                  ''
                );
              } catch {
                workTypeWitnessSignImageBySlice[sliceKey] = null;
              }
            } else {
              workTypeWitnessSignImageBySlice[sliceKey] = null;
            }
          }

          const BuildPrintDataForWorkTypeSlice = (slice) => {
            const sliceNm = GetSliceNm(slice);
            const sliceKey = GetSliceKey(slice);
            const detailsSlice = GetSliceDetails(slice);
            const { extSmNm: detExtSmNm, extSmSignPath: detExtSmSignPath } =
              PickDetailExtSmFields(detailsSlice);
            //작업 시작·종료(EndTime) / 연장 종료(ExtEndTime 있는 행만) 집계
            const {
              strTime: sliceStrTime,
              endTime: sliceEndTime,
              extEndTime: sliceExtEndTime,
            } = AggregateDetailStrAndEndTimes(detailsSlice);
            const workDateTimeFromDetails =
              BuildWorkDateTimeTextFromDetails(detailsSlice);
            const extensionDateTimeFromDetails =
              BuildExtensionDateTimeTextFromDetails(detailsSlice);
            const workDateTimeText =
              workDateTimeFromDetails ||
              [
                [mstStartDateYmd, sliceStrTime].filter(Boolean).join(' '),
                [mstEndDateYmd, sliceEndTime].filter(Boolean).join(' '),
              ]
                .filter((s) => s.length > 0)
                .join(' ~ ');
            const extensionDateTimeText =
              extensionDateTimeFromDetails ||
              (String(sliceExtEndTime ?? '').trim()
                ? [
                    [mstStartDateYmd, sliceStrTime].filter(Boolean).join(' '),
                    [mstEndDateYmd, sliceExtEndTime].filter(Boolean).join(' '),
                  ]
                    .filter((s) => s.length > 0)
                    .join(' ~ ')
                : '');

            const riskMergedPage = MergePerNewRiskItems(detailsSlice);
            const equipMergedPage = MergePerNewEquipItems(detailsSlice);
            const prepMergedPage = MergePerNewPrepItems(detailsSlice);

            const catalog = perNewR2CatalogRef.current;
            const workItemId = FindWorkItemIdForSlice(
              sliceNm,
              catalog?.workTypes
            );
            const riskMergedMap = new Map(
              riskMergedPage.map((x) => [x.code, x])
            );
            const equipMergedMap = new Map(
              equipMergedPage.map((x) => [x.code, x])
            );
            const prepMergedMap = new Map(
              prepMergedPage.map((x) => [x.code, x])
            );

            let riskRowData;
            let equipmentRowData;
            let preparationTableData;

            if (
              workItemId &&
              catalog &&
              Array.isArray(catalog.riskCodes) &&
              catalog.riskCodes.length > 0
            ) {
              const risksForWt = catalog.riskCodes.filter(
                (r) => String(r.workItemId ?? r.WorkItemId ?? '') === workItemId
              );
              const equipsForWt = (catalog.equipCodes ?? []).filter(
                (e) => String(e.workItemId ?? e.WorkItemId ?? '') === workItemId
              );
              const prepsForWt = (catalog.preparationCodes ?? []).filter(
                (p) => String(p.workItemId ?? p.WorkItemId ?? '') === workItemId
              );
              riskRowData =
                risksForWt.length > 0
                  ? BuildRiskSlotsFromMasterAndDetail(risksForWt, riskMergedMap)
                  : BuildRiskSlotsFallbackFromMergedOnly(riskMergedPage);
              equipmentRowData =
                equipsForWt.length > 0
                  ? BuildEquipSlotsFromMasterAndDetail(
                      equipsForWt,
                      equipMergedMap
                    )
                  : BuildEquipSlotsFallbackFromMergedOnly(equipMergedPage);
              preparationTableData =
                prepsForWt.length > 0
                  ? BuildPreparationTableFromMasterAndDetail(
                      prepsForWt,
                      prepMergedMap
                    )
                  : prepMergedPage
                      .filter((item) => item && item.code && item.name)
                      .map((prepItem) => ({
                        PREP_CODE: prepItem.code || '',
                        PREP_NAME: prepItem.name || '',
                        PREP_NEED: !!prepItem.need,
                        PREP_NOT_NEED: !!prepItem.notNeed,
                        PREP_CONFIRMED: PrepConfirmedReportString(
                          !!prepItem.witnessConfirmed
                        ),
                        PREP_WITNESS_CHECK: PrepWitnessCheckReportCode(
                          prepItem.witnessCheckRaw
                        ),
                        PREP_CHECKED: !!prepItem.need,
                        WORKER_NAME: '',
                      }));
            } else {
              riskRowData =
                BuildRiskSlotsFallbackFromMergedOnly(riskMergedPage);
              equipmentRowData =
                BuildEquipSlotsFallbackFromMergedOnly(equipMergedPage);
              const validPreparationItems = prepMergedPage.filter(
                (item) => item && item.code && item.name
              );
              preparationTableData = validPreparationItems.map((prepItem) => ({
                PREP_CODE: prepItem.code || '',
                PREP_NAME: prepItem.name || '',
                PREP_NEED: !!prepItem.need,
                PREP_NOT_NEED: !!prepItem.notNeed,
                PREP_CONFIRMED: PrepConfirmedReportString(
                  !!prepItem.witnessConfirmed
                ),
                PREP_WITNESS_CHECK: PrepWitnessCheckReportCode(
                  prepItem.witnessCheckRaw
                ),
                PREP_CHECKED: !!prepItem.need,
                WORKER_NAME: '',
              }));
            }

            const riskTableDataHorizontal = [riskRowData];
            const equipmentTableDataHorizontal = [equipmentRowData];

            const titleNm = sliceNm || workTypeNameJoined;
            const idxInList = sliceNm
              ? workItemNames.findIndex((n) => n === sliceNm)
              : -1;
            const wtKey =
              idxInList >= 0
                ? ResolveWorkTypeWtKey(sliceNm, idxInList)
                : sliceNm
                  ? WORK_TYPE_CODE_NM_TO_WT[sliceNm] || ''
                  : '';

            const pageWtFlags = { ...emptyWtFlags };
            if (
              wtKey &&
              Object.prototype.hasOwnProperty.call(pageWtFlags, wtKey)
            ) {
              pageWtFlags[wtKey] = true;
            }

            const TableDataMain = [
              {
                ...emptyWtFlags,
                WORK_ITEM_NM: sliceNm,
                WORK_TYPE_NM: sliceNm,
                LINE_NO: 1,
                /** MST 일자 + StrTime/EndTime(작업) — 연장은 EXTENSION_DATE_TIME */
                WORK_DATE_TIME: workDateTimeText,
                WorkDateTime: workDateTimeText,
                EXTENSION_DATE_TIME: extensionDateTimeText,
                ExtensionDateTime: extensionDateTimeText,
                ...(wtKey ? { [wtKey]: true } : {}),
              },
            ];

            const selectedRiskNames = riskMergedPage
              .filter((x) => x.checked)
              .map((x) => x.name)
              .filter(Boolean)
              .join(', ');
            const selectedEquipmentNames = equipMergedPage
              .filter((x) => x.checked)
              .map((x) => x.name)
              .filter(Boolean)
              .join(', ');
            const selectedPreparationNames = prepMergedPage
              .filter((x) => x.checked)
              .map((x) => x.name)
              .filter(Boolean)
              .join(', ');
            const etcRemarkPage = detailsSlice
              .map((d) => PickDetailEtcRemark(d))
              .filter((s) => String(s).trim() !== '')
              .join(' / ');
            const checkRemarkPage = detailsSlice
              .map(
                (d) => d.checkRemark ?? d.CheckRemark ?? d.CHECK_REMARK ?? ''
              )
              .filter(Boolean)
              .join(' / ');

            /** 상세(detail) 행의 증인 — MST witnessNm·witnessRank 등과 별도 */
            const pickDetailWitnessField = (d, keys) => {
              for (const k of keys) {
                const v = d[k];
                if (v != null && String(v).trim() !== '')
                  return String(v).trim();
              }
              return '';
            };
            const mergeDetailWitnessUnique = (keys) =>
              [
                ...new Set(
                  detailsSlice
                    .map((d) => pickDetailWitnessField(d, keys))
                    .filter(Boolean)
                ),
              ].join(' / ');
            const workTypeWitnessNm = mergeDetailWitnessUnique(['witnessNm']);
            const workTypeWitnessRank = mergeDetailWitnessUnique([
              'witnessRank',
            ]);
            const workTypeWitnessPosition = mergeDetailWitnessUnique([
              'witnessPosition',
            ]);
            const workTypeWitnessDepNm = mergeDetailWitnessUnique([
              'witnessDepNm',
            ]);
            const workTypeWitnessSignPath = mergeDetailWitnessUnique([
              'witnessSignPath',
              'WitnessSignPath',
              'WITNESS_SIGN_PATH',
            ]);

            const headerData = {
              pmCd: reportData.pmCd ?? '',
              custCd: reportData.custCd ?? '',
              depCd: reportData.depCd ?? '',
              reqId: reportData.reqId ?? '',
              reqNm: reportData.reqNm ?? '',
              reqDepNm: reportData.reqDepNm ?? '',
              reqRank: reportData.reqRank ?? '',
              reqPosition: reportData.reqPosition ?? '',
              reqSignPath: reportData.reqSignPath ?? '',
              ReqSignPath:
                reportData.reqSignPath ?? reportData.ReqSignPath ?? '',
              workItems: sliceNm || reportData.workItems || '',
              WORK_TYPE_NAME: titleNm,
              workTypeName: titleNm,
              totalStartDate: mstStartDateYmd,
              totalEndDate: mstEndDateYmd,
              strTime: sliceStrTime,
              steTime: sliceStrTime,
              endTime: sliceEndTime,
              extEndTime: sliceExtEndTime,
              workDateTimeText,
              extensionDateTimeText,
              WORK_DATE_TIME: workDateTimeText,
              WorkDateTime: workDateTimeText,
              EXTENSION_DATE_TIME: extensionDateTimeText,
              ExtensionDateTime: extensionDateTimeText,
              extSmNm:
                String(detExtSmNm ?? '').trim() ||
                String(reportData.extSmNm ?? '').trim() ||
                '',
              extSmSignPath:
                String(detExtSmSignPath ?? '').trim() ||
                String(reportData.extSmSignPath ?? '').trim() ||
                '',
              workLoc: reportData.workLoc ?? '',
              workContent: reportData.workContent ?? '',
              workMemberNo: reportData.workMemberNo ?? '',
              partnerNm: reportData.partnerNm ?? '',
              partnerManager: reportData.partnerManager ?? '',
              partnerMan: reportData.partnerManager ?? '',
              partnerTel: reportData.partnerTel ?? '',
              workerList: reportData.workerList ?? '',
              depSign: reportData.depSign ?? '',
              smSign: reportData.smSign ?? '',
              smId: reportData.smId ?? '',
              smNm: reportData.smNm ?? '',
              smDepNm: reportData.smDepNm ?? '',
              smRank: reportData.smRank ?? '',
              smPosition: reportData.smPosition ?? '',
              smSignPath: reportData.smSignPath ?? '',
              smSignDate: reportData.smSignDate ?? '',
              returnYn: reportData.returnYn ?? '',
              returnDetail: reportData.returnDetail ?? '',
              returnId: reportData.returnId ?? '',
              witnessId: reportData.witnessId ?? '',
              witnessYn: reportData.witnessYn ?? '',
              witnessNm: reportData.witnessNm ?? '',
              witnessSignPath: reportData.witnessSignPath ?? '',
              WitnessSignPath:
                reportData.witnessSignPath ?? reportData.WitnessSignPath ?? '',
              witnessDepNm: reportData.witnessDepNm ?? '',
              witnessRank: reportData.witnessRank ?? '',
              witnessPosition: reportData.witnessPosition ?? '',
              workTypeWitnessNm,
              workTypeWitnessRank,
              workTypeWitnessPosition,
              workTypeWitnessDepNm,
              workTypeWitnessSignPath,
              checkRemark: checkRemarkPage || '',
              status: reportData.status ?? '',
              createId: reportData.createId ?? '',
              createDate: reportData.createDate ?? '',
              updateId: reportData.updateId ?? '',
              updateDate: reportData.updateDate ?? '',

              permitDate,
              workItemCount: sliceNm ? 1 : workItemNames.length,
              ...pageWtFlags,

              // 서명 이미지 - 레포트에 바인딩하는 명칭
              REQ_SIGN_IMAGE: reqSignBase64 || null,
              SM_SIGN_IMAGE: smSignBase64 || null,
              EXT_SM_SIGN_IMAGE: extSmSignImageBySlice[sliceKey] || null,
              WITNESS_SIGN_IMAGE: witnessSignBase64 || null,
              /** 상세(detail) 작업종별 증인 서명 — MST WITNESS_SIGN_IMAGE와 별도 */
              WORKTYPE_WITNESS_SIGN_IMAGE:
                workTypeWitnessSignImageBySlice[sliceKey] || null,

              smSignDateYmd: formatDateYmd(reportData.smSignDate),
              selectedRiskNames,
              selectedEquipmentNames,
              selectedPreparationNames,
              etcRemark: etcRemarkPage,
              etc_remark: etcRemarkPage,
              ETC_REMARK: etcRemarkPage,
              detailRowCount: detailsSlice.length,
              dailyCount:
                env.dailiesByPmCd?.length ??
                env.dailiesForView?.length ??
                env.dailies.length,
              centerSignCount: env.centerSigns.length,
            };

            if (sliceNm) {
              headerData.workItemNm1 = sliceNm;
              headerData.workTypeNm1 = sliceNm;
              if (wtKey) {
                headerData.workTypeWt1 = wtKey;
                headerData.workTypeCd1 = WORK_TYPE_WT_TO_CD[wtKey] || '';
              }
            } else {
              workItemNames.forEach((nm, i) => {
                const idx = i + 1;
                const wk = ResolveWorkTypeWtKey(nm, i);
                headerData[`workItemNm${idx}`] = nm;
                headerData[`workTypeNm${idx}`] = nm;
                if (wk) {
                  headerData[`workTypeWt${idx}`] = wk;
                  headerData[`workTypeCd${idx}`] = WORK_TYPE_WT_TO_CD[wk] || '';
                }
              });
            }
            return {
              FileName: 'PER_NEW',
              HeaderData: JSON.stringify(headerData),
              TableDataMain,
              TableData1: JSON.stringify(riskTableDataHorizontal),
              TableData2: JSON.stringify(equipmentTableDataHorizontal),
              TableData3: JSON.stringify(preparationTableData),
              TableData4: JSON.stringify(centerTableRows),
              TableData5: JSON.stringify([]),
              TableData6: JSON.stringify([]),
            };
          };

          const pdfBlobs = [];
          for (const slice of slices) {
            const printData = BuildPrintDataForWorkTypeSlice(slice);
            const response = await Commons.Axios(
              'report',
              Global.base.useUrl + Global.urls.Reports.reports,
              printData
            );
            pdfBlobs.push(response.data);
          }

          let finalBlob = pdfBlobs[0];
          if (pdfBlobs.length > 1) {
            const mergedPdf = await PDFDocument.create();
            for (const b of pdfBlobs) {
              const pdf = await PDFDocument.load(await b.arrayBuffer());
              const copied = await mergedPdf.copyPages(
                pdf,
                pdf.getPageIndices()
              );
              copied.forEach((p) => mergedPdf.addPage(p));
            }
            const mergedBytes = await mergedPdf.save();
            finalBlob = new Blob([mergedBytes], { type: 'application/pdf' });
          }

          const blobUrl = URL.createObjectURL(finalBlob);

          if (attachPreviewToMaster) {
            if (lastPreviewBlobUrlRef.current) {
              try {
                URL.revokeObjectURL(lastPreviewBlobUrlRef.current);
              } catch {
                /* ignore */
              }
            }
            lastPreviewBlobUrlRef.current = blobUrl;
            setSelectedRowData1((prev) => {
              if (prev.length === 0) return prev;
              const pmCd = reportData.pmCd || '';
              return [
                {
                  ...prev[0],
                  saveName: `안전작업허가서_${pmCd}.pdf`,
                  filePath: blobUrl,
                  previewFilePath: blobUrl,
                },
              ];
            });
          }
          if (openPdfWindow && blobUrl) {
            window.open(blobUrl, '_blank', 'noopener,noreferrer');
          }
        } catch (e) {
          console.error('레포트 생성 오류:', e);
          dispatch(
            showSnackbar({
              message: '레포트 생성 중 오류가 발생했습니다.',
              type: 'error',
            })
          );
        }
      };

      executePermitPdfRef.current = executePermitPdf;

      // 목록 행 클릭 미리보기: 세부내역(DTL) 1건당 PER_NEW 1장 생성 후 pdf-lib 병합
      const previewDetailRows = (detailRows || []).filter((d) =>
        String(d?.dtlCd ?? d?.DtlCd ?? d?.DTL_CD ?? '').trim()
      );
      if (previewDetailRows.length === 0) {
        dispatch(
          showSnackbar({
            message: '미리보기할 작업 세부내역이 없습니다.',
            type: 'warning',
          })
        );
        return;
      }
      await executePermitPdf(detailRows, {
        mode: 'selection',
        selectedRows: previewDetailRows,
        attachPreviewToMaster: true,
        openPdfWindow: false,
      });
      } finally {
        setPermitPreviewLoading(false);
      }
    },
  });

  /** 상세 조회용 작업일자 — 삭제된 일자가 아닌 남은 일자 우선 */
  const ResolveWorkDateForDetailSearch = (row) => {
    const pmCd = String(row?.pmCd ?? row?.PmCd ?? row?.PM_CD ?? '').trim();
    const master = (rowData1 || []).find(
      (r) => String(r.pmCd ?? r.PmCd ?? r.PM_CD ?? '').trim() === pmCd
    );
    const detailDates = (master?.detailRows || [])
      .map((d) => PickPermitWorkDateYmd(d))
      .filter(Boolean);
    const candidates = [
      ...detailDates,
      PickPermitWorkDateYmd(row),
      PickPermitWorkDateYmd({ workDate: row?.totalStartDate }),
      PickPermitWorkDateYmd({ workDate: row?.createDate }),
    ].filter(Boolean);
    const unique = [...new Set(candidates)];
    return unique[0] || '';
  };

  const SearchDetailLogic = async (row) => {
    const pmCd = row?.pmCd ?? row?.PmCd ?? row?.PM_CD ?? '';
    const workDate = ResolveWorkDateForDetailSearch(row);
    if (!pmCd) return;
    if (!workDate) {
      dispatch(
        showSnackbar({
          message: '작업일자가 없어 상세 정보를 조회할 수 없습니다.',
          type: 'warning',
        })
      );
      return;
    }

    const cacheKey = BuildDetailPreviewCacheKey(pmCd, workDate);
    if (
      detailPreviewCacheKeyRef.current === cacheKey &&
      perNewPrintCacheRef.current?.env
    ) {
      RestoreMasterExpanded(pmCd);
      return;
    }
    if (search2InFlightRef.current === cacheKey) {
      return;
    }
    search2InFlightRef.current = cacheKey;

    const loadingTimer = window.setTimeout(
      () => setPermitPreviewLoading(true),
      280
    );
    try {
      await Search2({
        row: { pmCd, workDate },
        userInfo,
      });
      detailPreviewCacheKeyRef.current = cacheKey;
    } catch {
      window.clearTimeout(loadingTimer);
      setPermitPreviewLoading(false);
    } finally {
      if (search2InFlightRef.current === cacheKey) {
        search2InFlightRef.current = '';
      }
    }
  };

  const handleRowSelection1 = (rowData, rowIndex) => {
    // 이전에 클릭한 행(seq 기준)을 기억
    // if (clickRowId === rowData.no) return; // 같은 행이면 무시

    setClickRowId(rowData.no); // 현재 클릭한 행 ID 저장

    currentRowIndexRef1.current = rowIndex;
    // 체크박스 선택 상태는 onSelectionChanged에서 관리하므로 여기서는 업데이트하지 않음
    // setSelectedRowData1은 onSelectionChanged에서만 업데이트되어 다중 선택이 유지됨

    // 행 클릭 시 상세 조회 (api/PerNew/GetPerNewR3: PmCd, WorkDate, UserId)
    const pmCd = rowData.pmCd || rowData.PM_CD || rowData.PmCd;
    if (pmCd) {
      RestoreMasterExpanded(pmCd);
      SearchDetailLogic(rowData);
    }
  };
  const ResetLogic1 = () => {
    setRowData1([]);
    setDetailTitle2('작업 세부내역');
    setSelectedRowData1([]);
    setChangedRowData1([]);
    setDeletedRowData1([]);
    perNewPrintCacheRef.current = null;
    perNewR2CatalogRef.current = null;
    perNewR2CatalogCustRef.current = '';
    detailPreviewCacheKeyRef.current = '';
    search2InFlightRef.current = '';
    if (lastPreviewBlobUrlRef.current) {
      try {
        URL.revokeObjectURL(lastPreviewBlobUrlRef.current);
      } catch {
        /* ignore */
      }
      lastPreviewBlobUrlRef.current = '';
    }
    executePermitPdfRef.current = null;
    detailApisRef.current = new Map();
    activePmCdRef.current = '';
    expandedPmCdRef.current = '';
    setPermitPreviewLoading(false);
  };

  const fixUrl = (url) => {
    if (!url || typeof url !== 'string') return ''; // 또는 적절한 fallback
    const parts = url.split('/');
    const fileName = parts.pop(); // 파일 이름 분리
    const encodedFileName = encodeURIComponent(fileName);
    return [...parts, encodedFileName].join('/');
  };

  // 반려이력 조회 팝업 (상세 영역)
  const OpenRejectHistory = async (_PM_CD) => {
    setModalToggle3(true);
    await SearchReturn(_PM_CD);
  };

  // 코드관리 팝업 열기
  const HandleCodeManagementClick = () => {
    if (!props.searchRef?.current?.custCd) {
      dispatch(
        showSnackbar({
          message: '사업장을 선택해주세요.',
          type: 'warning',
        })
      );
      return;
    }
    setCodeManagementOpen(true);
  };

  // 코드관리 저장
  const { runAsync: SaveWorkTypeItemsAsync } = useSaveWorkTypeItems({
    userInfo,
    search: {
      custCd: props.searchRef?.current?.custCd || '',
    },
  });

  const HandleCodeManagementSave = async (data) => {
    // data가 객체 형태인지 배열 형태인지 확인 (초기 데이터 비교 포함 여부)
    const currentData = data.current || data;
    const initialData = data.initial || [];

    // currentData와 initialData가 모두 비어있을 때만 오류 표시
    if (
      (!currentData || currentData.length === 0) &&
      (!initialData || initialData.length === 0)
    ) {
      dispatch(
        showSnackbar({
          message: '저장할 데이터가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }

    if (!props.validateSelection()) {
      return;
    }

    setLoading(true);
    try {
      // 저장 데이터 준비 (서버 API 형식에 맞춤)
      const saveDataList = [];
      const deleteDataList = [];
      const custCd = props.searchRef?.current?.custCd || '';

      // 삭제된 작업종류 찾기 (initialData에는 있지만 currentData에는 없는 경우)
      const currentWorkTypeCds = (currentData || []).map(
        (item) => item.workTypeCd
      );
      const deletedWorkTypes = (initialData || []).filter(
        (initialItem) => !currentWorkTypeCds.includes(initialItem.workTypeCd)
      );

      // 삭제 데이터 구성
      for (const deletedItem of deletedWorkTypes) {
        deleteDataList.push({
          custCd: custCd,
          workTypeCd: deletedItem.workTypeCd,
        });
      }

      // 각 작업종류별로 저장 데이터 구성
      for (const workTypeItem of currentData || []) {
        // 초기 데이터에서 같은 작업종류 찾기 (삭제 비교용)
        const initialItem = initialData.find(
          (item) => item.workTypeCd === workTypeItem.workTypeCd
        );

        // workItemId가 있으면 기존 데이터이므로, 모든 항목이 삭제된 경우도 저장해야 함
        // (서버에서 삭제 로직 실행을 위해 빈 배열도 전송)

        // 위험사항: 이름 배열 순서대로 매핑에서 코드 찾기 (순서 보장)
        const hazardousItems = (workTypeItem.hazardousItems || [])
          .filter((item) => item && item.trim())
          .map((item) => item.trim());

        // 초기 위험사항 이름 배열 (삭제 비교용)
        const initialHazardousItems = initialItem
          ? (initialItem.hazardousItems || [])
              .filter((item) => item && item.trim())
              .map((item) => item.trim())
          : [];

        // 현재 매핑에서 코드 찾기
        const hazardousItemMap = workTypeItem.hazardousItemMap || [];
        // 초기 매핑 (삭제된 항목의 코드를 찾기 위해)
        const initialHazardousItemMap = initialItem?.hazardousItemMap || [];
        // 초기 코드 배열 (백업용 - 매핑이 없을 때 사용)
        const initialRiskCodes = initialItem?.hazardousItemCodes || [];

        // 현재 이름 배열에 있는 항목의 코드 찾기 (코드를 문자열로 일관되게 변환, 공백 제거)
        const currentRiskCodes = hazardousItems
          .map((name) => {
            const trimmedName = name.trim();
            const mapped = hazardousItemMap.find((m) => {
              if (!m || !m.name) return false;
              return m.name.trim() === trimmedName;
            });
            return mapped?.code ? String(mapped.code).trim() : null;
          })
          .filter((code) => code != null && code !== '')
          .map((code) => code.trim()); // 추가 공백 제거

        // 초기에는 있었지만 현재에는 없는 항목 찾기 (삭제된 항목)
        // 정확한 문자열 비교를 위해 trim 적용
        const deletedHazardousItems = initialHazardousItems.filter(
          (initialName) => {
            const trimmedInitial = initialName.trim();
            return !hazardousItems.some(
              (currentName) => currentName.trim() === trimmedInitial
            );
          }
        );

        // 삭제된 항목의 코드를 찾기 (초기 매핑 또는 초기 코드 배열 사용)
        const deletedRiskCodes = deletedHazardousItems
          .map((deletedName, deletedIndex) => {
            const trimmedDeleted = deletedName.trim();

            // 1순위: 초기 매핑에서 이름으로 찾기
            const mappedFromMap = initialHazardousItemMap.find(
              (m) => m && m.name && m.name.trim() === trimmedDeleted
            );

            if (mappedFromMap?.code) {
              return String(mappedFromMap.code).trim();
            }

            // 2순위: 초기 코드 배열에서 인덱스로 찾기 (초기 이름 배열의 인덱스와 일치)
            const initialIndex = initialHazardousItems.findIndex(
              (name) => name.trim() === trimmedDeleted
            );
            if (initialIndex >= 0 && initialIndex < initialRiskCodes.length) {
              const code = initialRiskCodes[initialIndex];
              return code ? String(code).trim() : null;
            }

            return null;
          })
          .filter((code) => code != null && code !== '');

        // 최종 코드 리스트: 현재 코드만 포함, 콤마 사이 공백 없이 (삭제된 코드는 제외)
        // 서버는 기존 코드 리스트와 현재 코드 리스트를 비교하여 없는 코드를 삭제합니다.
        const riskCodeList = currentRiskCodes
          .map((code) => code.trim())
          .join(',');

        // 위험사항 이름 리스트 추출
        const riskNmList = hazardousItems.join(',');

        // 안전보호구: 이름 배열 순서대로 매핑에서 코드 찾기 (순서 보장)
        const safetyEquipment = (workTypeItem.safetyEquipment || [])
          .filter((item) => item && item.trim())
          .map((item) => item.trim());

        // 초기 안전보호구 이름 배열 (삭제 비교용)
        const initialSafetyEquipment = initialItem
          ? (initialItem.safetyEquipment || [])
              .filter((item) => item && item.trim())
              .map((item) => item.trim())
          : [];

        // 현재 매핑에서 코드 찾기
        const safetyEquipmentMap = workTypeItem.safetyEquipmentMap || [];
        // 초기 매핑 (삭제된 항목의 코드를 찾기 위해)
        const initialSafetyEquipmentMap = initialItem?.safetyEquipmentMap || [];
        // 초기 코드 배열 (백업용 - 매핑이 없을 때 사용)
        const initialEquipCodes = initialItem?.safetyEquipmentCodes || [];

        // 현재 이름 배열에 있는 항목의 코드 찾기 (코드를 문자열로 일관되게 변환, 공백 제거)
        const currentEquipCodes = safetyEquipment
          .map((name) => {
            const trimmedName = name.trim();
            const mapped = safetyEquipmentMap.find((m) => {
              if (!m || !m.name) return false;
              return m.name.trim() === trimmedName;
            });
            return mapped?.code ? String(mapped.code).trim() : null;
          })
          .filter((code) => code != null && code !== '')
          .map((code) => code.trim()); // 추가 공백 제거

        // 초기에는 있었지만 현재에는 없는 항목 찾기 (삭제된 항목)
        // 정확한 문자열 비교를 위해 trim 적용
        const deletedSafetyEquipment = initialSafetyEquipment.filter(
          (initialName) => {
            const trimmedInitial = initialName.trim();
            return !safetyEquipment.some(
              (currentName) => currentName.trim() === trimmedInitial
            );
          }
        );

        // 삭제된 항목의 코드를 찾기 (초기 매핑 또는 초기 코드 배열 사용)
        const deletedEquipCodes = deletedSafetyEquipment
          .map((deletedName) => {
            const trimmedDeleted = deletedName.trim();

            // 1순위: 초기 매핑에서 이름으로 찾기
            const mappedFromMap = initialSafetyEquipmentMap.find(
              (m) => m && m.name && m.name.trim() === trimmedDeleted
            );

            if (mappedFromMap?.code) {
              return String(mappedFromMap.code).trim();
            }

            // 2순위: 초기 코드 배열에서 인덱스로 찾기 (초기 이름 배열의 인덱스와 일치)
            const initialIndex = initialSafetyEquipment.findIndex(
              (name) => name.trim() === trimmedDeleted
            );
            if (initialIndex >= 0 && initialIndex < initialEquipCodes.length) {
              const code = initialEquipCodes[initialIndex];
              return code ? String(code).trim() : null;
            }

            return null;
          })
          .filter((code) => code != null && code !== '');

        // 최종 코드 리스트: 현재 코드만 포함, 콤마 사이 공백 없이 (삭제된 코드는 제외)
        // 서버는 기존 코드 리스트와 현재 코드 리스트를 비교하여 없는 코드를 삭제합니다.
        const workEquipCodeList = currentEquipCodes
          .map((code) => code.trim())
          .join(',');

        // 안전보호구 이름 리스트 추출
        const workEquipNmList = safetyEquipment.join(',');

        // 준비사항 처리 (서버 로직에 맞춤: 코드와 이름을 인덱스로 매칭)
        // 현재 준비사항 배열 (코드|이름 형태 또는 이름만)
        const currentPreparations = (workTypeItem.preparations || [])
          .filter((prep) => prep && prep.trim())
          .map((prep) => prep.trim());

        // 초기 준비사항 배열 (삭제 비교용)
        // 초기 데이터는 이름 배열이므로 그대로 사용
        const initialPreparations = initialItem
          ? (initialItem.preparations || [])
              .filter((prep) => prep && prep.trim())
              .map((prep) => prep.trim())
          : [];

        // 현재 매핑에서 코드 찾기
        const preparationMap = workTypeItem.preparationMap || [];
        // 초기 매핑 (삭제된 항목의 코드를 찾기 위해)
        const initialPreparationMap = initialItem?.preparationMap || [];

        // 서버 로직에 맞춰 코드 리스트와 이름 리스트를 인덱스로 매칭하여 구성
        // 서버는 코드 리스트와 이름 리스트를 인덱스로 매칭 (i번째 코드와 i번째 이름)
        // 코드가 있는 항목: 코드 리스트와 이름 리스트에 같은 인덱스로 추가
        // 이름만 있는 항목: 이름 리스트에만 추가 (서버가 이름 목록에만 있는 새로운 준비사항으로 처리)
        const preparationCodes = [];
        const preparationNames = [];

        // currentPreparations는 이름 배열 (preparations 배열에 이름만 저장됨)
        for (const prepName of currentPreparations) {
          const name = prepName.trim();

          if (!name || !name.trim()) continue;

          // 매핑에서 코드 찾기
          const mapped = preparationMap.find((m) => {
            if (!m || !m.name) return false;
            return m.name.trim() === name.trim();
          });

          const finalCode = mapped?.code ? String(mapped.code).trim() : null;

          // 코드가 있으면 코드 리스트와 이름 리스트에 같은 인덱스로 추가
          if (finalCode && finalCode !== '') {
            preparationCodes.push(finalCode);
            preparationNames.push(name.trim());
          } else {
            // 코드가 없으면 이름 리스트에만 추가
            // 서버 로직: 이름 목록에만 있는 새로운 준비사항 추가 (코드 목록보다 많은 경우)
            preparationNames.push(name.trim());
          }
        }

        // 서버 로직:
        // 1. 코드 리스트와 이름 리스트를 인덱스로 매칭 (i번째 코드와 i번째 이름)
        // 2. 이름 목록에만 있는 새로운 준비사항 추가 (코드 목록보다 많은 경우)
        // 따라서 코드 리스트와 이름 리스트를 분리하여 전송
        const preparationCodeList = preparationCodes
          .map((code) => code.trim())
          .join(',');
        const preparationNmList = preparationNames.join(',');

        // 삭제된 준비사항 찾기 (초기에는 있었지만 현재에는 없는 항목)
        // 서버 로직: 기존에 있지만 현재 코드 목록에 없는 항목을 삭제
        // 따라서 초기 데이터에서 코드가 있었던 항목 중, 현재 코드 목록에 없는 항목을 찾아야 함

        // 초기 준비사항에서 코드와 이름 추출
        // 초기 데이터는 이름 배열이므로, 초기 매핑을 사용하여 코드를 찾아야 함
        const initialPrepCodes = [];
        const initialPrepNames = [];

        for (const prepName of initialPreparations) {
          const name = prepName.trim();

          if (!name || !name.trim()) continue;

          // 초기 매핑에서 코드 찾기
          const mapped = initialPreparationMap.find((m) => {
            if (!m || !m.name) return false;
            return m.name.trim() === name.trim();
          });

          const finalCode = mapped?.code ? String(mapped.code).trim() : null;

          if (finalCode && finalCode !== '') {
            initialPrepCodes.push(finalCode);
            initialPrepNames.push(name.trim());
          } else {
            // 코드가 없는 경우도 이름은 추가 (서버에서 처리)
            initialPrepNames.push(name.trim());
          }
        }

        // 현재 코드 목록에 없는 초기 코드 찾기 (삭제 대상)
        // 서버는 현재 전송된 preparationCodeList에 없는 기존 코드를 삭제합니다.
        // 따라서 삭제된 항목의 코드가 preparationCodeList에 포함되지 않아야 합니다.
        const currentCodeSet = new Set(
          preparationCodes.map((code) => code.trim())
        );
        const deletedPreparationCodes = initialPrepCodes.filter(
          (initialCode) => !currentCodeSet.has(initialCode.trim())
        );

        // 삭제된 항목의 이름도 찾기 (로깅용)
        const deletedPreparations = initialPrepNames.filter(
          (initialName, idx) => {
            if (idx < initialPrepCodes.length) {
              const initialCode = initialPrepCodes[idx];
              return !currentCodeSet.has(initialCode.trim());
            }
            return false;
          }
        );

        // 서버 API 형식으로 저장 데이터 구성 (스키마 매퍼에서 암호화 처리)
        const saveItem = {
          workItemId: workTypeItem.workItemId || '', // 기존 데이터인 경우에만
          custCd: custCd,
          workType: workTypeItem.workTypeCd,
          riskCodeList: riskCodeList,
          riskNmList: riskNmList,
          workEquipCodeList: workEquipCodeList,
          workEquipNmList: workEquipNmList,
          preparationCodeList: preparationCodeList,
          preparationNmList: preparationNmList,
        };

        saveDataList.push(saveItem);
      }

      // 저장할 항목과 삭제할 항목이 모두 없으면 오류
      // 단, 작업종류를 모두 삭제한 경우(deleteDataList만 있는 경우)는 허용
      if (saveDataList.length === 0 && deleteDataList.length === 0) {
        dispatch(
          showSnackbar({
            message: '저장할 항목이 없습니다.',
            type: 'warning',
          })
        );
        setLoading(false);
        return;
      }

      // 저장 실행 (삭제 먼저 처리, 그 다음 저장)
      try {
        const result = await SaveWorkTypeItemsAsync({
          saveItems: saveDataList,
          deleteItems: deleteDataList.length > 0 ? deleteDataList : [],
        });

        // 응답 검증 (배치 응답 처리)
        // apiRequest는 axios response의 data를 반환하므로 result가 배열 또는 객체일 수 있음
        // BuildBatchResponse는 배열로 반환됨
        let responseData = result;

        // result가 객체이고 data 속성이 있는 경우
        if (
          result &&
          typeof result === 'object' &&
          !Array.isArray(result) &&
          result.data
        ) {
          responseData = result.data;
        }

        if (responseData) {
          const results = Array.isArray(responseData)
            ? responseData
            : [responseData];

          // 각 결과 검증
          results.forEach((r, idx) => {});

          const errorResult = results.find(
            (r) => !r.isSuccess || r.type === 'ERROR'
          );

          if (errorResult) {
            dispatch(
              showSnackbar({
                message:
                  errorResult.message ||
                  errorResult.errorMsg ||
                  '저장 중 오류가 발생했습니다.',
                type: 'error',
              })
            );
            setLoading(false);
            return;
          }

          // 모든 결과가 성공인지 확인
          const allSuccess = results.every(
            (r) => r.isSuccess !== false && r.type !== 'ERROR'
          );
          if (!allSuccess) {
            console.warn('일부 결과가 성공하지 않았습니다:', results);
          }
        } else {
          console.warn('저장 응답에 데이터가 없습니다:', result);
        }
      } catch (error) {
        console.error('저장 중 예외 발생:', error);
        console.error('에러 상세:', {
          message: error.message,
          response: error.response,
          stack: error.stack,
        });
        dispatch(
          showSnackbar({
            message:
              error.message ||
              error.response?.data?.message ||
              '저장 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
        setLoading(false);
        return;
      }

      // 성공 처리
      dispatch(
        showSnackbar({
          message: '작업 종류별 항목이 저장되었습니다.',
          type: 'success',
        })
      );
      setCodeManagementOpen(false);

      // 액션 로그 저장
      await SaveActionLog([], {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'SUCCESS',
      });
    } catch (e) {
      console.error(e);
      dispatch(
        showSnackbar({
          message:
            e.message || '오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
          type: 'error',
        })
      );

      // 에러 로그 저장
      await SaveActionLog([], {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'ERROR',
        errorMsg: e.message,
      });
    } finally {
      setLoading(false);
    }
  };
  //#endregion

  return (
    <div className="wrap">
      <SAM_BASIC_02
        gridId1={'grid1'}
        navigate={navigate}
        userInfo={userInfo}
        rowData1={rowData1}
        detailTitle2={detailTitle2}
        lanInfo={lanInfo}
        gridRef1={gridRef1}
        gridRef4={gridRef4}
        setRowData1={setRowData1}
        selectedRowData1={selectedRowData1}
        permitPreviewLoading={permitPreviewLoading}
        setSelectedRowData1={setSelectedRowData1}
        changedRowData1={changedRowData1}
        setChangedRowData1={setChangedRowData1}
        selectedRowNosRef1={selectedRowNosRef1}
        lastCheckedIndexRef1={lastCheckedIndexRef1}
        searchRef={props.searchRef}
        dispatch={dispatch}
        showSnackbar={showSnackbar}
        modalToggle1={modalToggle1}
        setModalToggle1={setModalToggle1}
        setRejectTargetRows={setRejectTargetRows}
        modalToggle2={modalToggle2}
        setModalToggle2={setModalToggle2}
        approvalTargetRows={approvalTargetRows}
        setApprovalTargetRows={setApprovalTargetRows}
        pendingApprovalGubun={pendingApprovalGubun}
        setPendingApprovalGubun={setPendingApprovalGubun}
        modalToggle3={modalToggle3}
        setModalToggle3={setModalToggle3}
        modalRowData3={modalRowData3}
        setModalRowData3={setModalRowData3}
        selectedModalRowData3={selectedModalRowData3}
        setSelectedModalRowData3={setSelectedModalRowData3}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        signInputs={signInputs}
        setSignInputs={setSignInputs}
        signatureCanvas={signatureCanvas}
        // 함수
        validateSelection={props.validateSelection}
        SearchLogic1={SearchLogic1}
        SearchLogic2={SearchLogic2}
        SearchLogic3={SearchLogic3}
        SearchReturn={SearchReturn}
        RejectLogic={RejectLogic}
        ApproveLogic={ApproveLogic}
        SaveLogic2={SaveLogic2}
        SaveLogic3={SaveLogic3}
        SaveLogicPreview={SaveLogicPreview}
        DownloadLogic1={DownloadLogic1}
        DownloadLogic2={DownloadLogic2}
        handleRowSelection1={handleRowSelection1}
        RestoreRow1={deleteHandler1.RestoreRow}
        updateCurrentRowData={updateCurrentRowData}
        DeleteNewRow1={deleteHandler1.DeleteNewRow}
        onRejectHistoryClick={OpenRejectHistory}
        handleCodeManagementClick={HandleCodeManagementClick}
        registerDetailApi={RegisterDetailApi}
        setActivePmCd={SetActivePmCd}
        masterListRefreshKey={masterListRefreshKey}
        expandedPmCdRef={expandedPmCdRef}
        onDeleteDetailClick={DeleteDetailLogic}
        onRejectDetailClick={TriggerDetailReject}
        onApproveDetailClick={TriggerDetailApprove}
        deleteMasterConfirmOpen={deleteMasterConfirmOpen}
        setDeleteMasterConfirmOpen={setDeleteMasterConfirmOpen}
        deleteMasterConfirmCount={deleteMasterConfirmCount}
        onDeleteMasterPermitClick={OpenDeleteMasterPermitConfirm}
        onConfirmDeleteMasterPermit={DeleteMasterLogic}
      />
      <WorkTypeItemManagement
        open={codeManagementOpen}
        onClose={() => setCodeManagementOpen(false)}
        onSave={HandleCodeManagementSave}
        custCd={props.searchRef?.current?.custCd || ''}
        userInfo={userInfo}
      />
      <Loading open={loading} />
    </div>
  );
}

export default SAM_BASIC_02F;
